import { getValidationError } from "../consignment/ConsignmentValidationErrors";
import ConsignmentImports from "../consignment/ConsignmentImports";
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Icon,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import _ from "lodash";
import moment from "moment";
import "moment/locale/en-gb";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  notifyShowErrorMessage,
  notifyShowSuccessMessage,
  triggerRerender,
} from "../../common/CommonActions";
import { ErrorMessageResolver } from "../../common/ErrorMessageResolver";
import { LOCALE } from "../../properties/Locale";
import { ConsignmentRepository } from "../../repositories/ConsignmentRepository";
import { CountriesV2Repository } from "../../repositories/CountriesV2Repository";
import { OrganizationsRepository } from "../../repositories/OrganizationsRepository";
import { CarrierRepository } from "../../repositories/CarrierRepository";
import { AddCircleOutline } from "@mui/icons-material";
import {
  ConsignmentValidator,
  ConsignmentValidatorEU,
} from "../consignment/ConsignmentValidator";
import CreateCarrierFromConsignmentDialog from "../consignment/CreateCarrierFromConsignmentDialog";
import GoodsItemsSection from "../consignment/GoodsItemsSection";
import { ShipsCodebookRepository } from "../../repositories/ShipsCodebookRepository";

import { AlertRepository } from "../../repositories/AlertRrepository";

/** -------- Helpers for multi-house support w/ legacy compatibility -------- */

function generateFourDigitNumber() {
  return Math.floor(1000 + Math.random() * 9000);
}

function defaultGoodsRow() {
  return {
    numberOfPackages: 0,
    packageType: null,
    hsCode: "",
    description: "",
    weight: 0,
  };
}

function defaultHouse() {
  return {
    mainTransportDocument: "N990",
    mrnReference: "", // NEW
    consignor: {},
    seller: {},
    buyer: {},
    consignee: {},
    goodsList: [defaultGoodsRow()],
  };
}

/**
 * Accepts either legacy or new consignment shapes and ALWAYS returns the new shape.
 * Legacy root fields (consignor/seller/buyer/consignee/goodsList/mainTransportDocument)
 * are migrated into houses[0].
 */
function normalizeConsignment(c) {
  if (!c) return c;

  if (Array.isArray(c.houses) && c.houses.length > 0) return c;

  const migratedHouse = {
    mainTransportDocument: c.mainTransportDocument ?? "N990",
    mrnReference: c.mrnReference ?? null, // NEW
    consignor: c.consignor ?? {},
    seller: c.seller ?? {},
    buyer: c.buyer ?? {},
    consignee: c.consignee ?? {},
    goodsList:
      Array.isArray(c.goodsList) && c.goodsList.length > 0
        ? c.goodsList
        : [defaultGoodsRow()],
  };

  const cleaned = { ...c };
  delete cleaned.mainTransportDocument;
  delete cleaned.consignor;
  delete cleaned.seller;
  delete cleaned.buyer;
  delete cleaned.consignee;
  delete cleaned.goodsList;

  return {
    ...cleaned,
    houses: [migratedHouse],
  };
}

/**
 * Build a payload that works for BOTH backends during transition:
 * - Always includes `houses` (new API)
 * - Also fills legacy root fields from House #1 (for old API)
 */
function preparePayloadForSaveGB(cons) {
  const out = { ...cons };

  if (!Array.isArray(out.houses) || out.houses.length === 0) {
    out.houses = [defaultHouse()];
  }

  out.houses = out.houses.map((h) => ({
    ...h,
    goodsList: (h.goodsList ?? []).map((g) => ({
      ...g,
      weight:
        g.weight === "" ||
        g.weight === null ||
        g.weight === undefined
          ? null
          : Number(g.weight),
    })),
  }));

  const h0 = out.houses[0] ?? defaultHouse();

  // Legacy compatibility: mirror first house to root (for old servers)
  out.mainTransportDocument = h0.mainTransportDocument;
  out.mrnReference = h0.mrnReference; // NEW

  out.consignor = h0.consignor;
  out.consignee = h0.consignee;

  out.seller = h0.seller;
  out.buyer = h0.buyer;
  out.goodsList = h0.goodsList;

  return out;
}

function preparePayloadForSaveEU(cons) {
  const out = { ...cons };

  if (!Array.isArray(out.houses) || out.houses.length === 0) {
    out.houses = [defaultHouse()];
  }

  out.houses = out.houses.map((h) => ({
    ...h,
    goodsList: (h.goodsList ?? []).map((g) => ({
      ...g,
      weight:
        g.weight === "" ||
        g.weight === null ||
        g.weight === undefined
          ? null
          : Number(g.weight),
    })),
  }));

  const h0 = out.houses[0] ?? defaultHouse();
  // Legacy compatibility: mirror first house to root (for old servers)
  out.mainTransportDocument = h0.mainTransportDocument;
  //TODO: CHANGE THIS TO INPUT FROM MASTER MRN FIELD
  // out.mrnReference = h0.mrnReference; // NEW

  if (out.houses.length > 1) {
    //Multiple houses - get fields for consignor/consignee from carrier
    const carrier = out.carrier ?? {};

    out.consignor = {
      ...out.consignor,
      name: carrier.name ?? "",
      address: carrier.address ?? "",
      addressNumber: carrier.addressNumber ?? "",
      postCode: carrier?.postCode ?? "",
      city: carrier?.city ?? null,
      country: carrier?.country ?? null,
      eori: carrier?.eori ?? null,
    };

    out.consignee = {
      ...out.consignee,
      name: carrier.name ?? "",
      address: carrier.address ?? "",
      addressNumber: carrier.addressNumber ?? "",
      postCode: carrier?.postCode ?? "",
      city: carrier?.city ?? null,
      country: carrier?.country ?? null,
      eori: carrier?.eori ?? null,
    };
  } else {
    const seller = h0.seller ?? {};
    const buyer = h0.buyer ?? {};

    out.consignor = {
      ...out.consignor,
      name: seller.name ?? "",
      address: seller.address ?? "",
      addressNumber: seller.addressNumber ?? "",
      postCode: seller?.postCode ?? "",
      city: seller?.city ?? null,
      country: seller?.country ?? null,
      eori: seller?.eori ?? null,
      poBoxNumber: seller?.poBoxNumber ?? null,
    };
    out.consignee = {
      ...out.consignee,
      name: buyer.name ?? "",
      address: buyer.address ?? "",
      addressNumber: buyer.addressNumber ?? "",
      postCode: buyer?.postCode ?? "",
      city: buyer?.city ?? null,
      country: buyer?.country ?? null,
      eori: buyer?.eori ?? null,
      poBoxNumber: buyer?.poBoxNumber ?? null,
    };
  }

  return out;
}
/** ------------------------------------------------------------------------ */

export const ENSDeclaration = ({
  data,
  ensDeclarationDialog,
  loadData,
  loadingData,
}) => {
  const [consignments, setConsignments] = useState();
  const [selectedConsignment, setSelectedConsignment] = useState();
  const [selectedHouseIndex, setSelectedHouseIndex] = useState(0);
  const [anchorElHouse, setAnchorElHouse] = useState(null);

  const mainTransportDocumentLabels = {
    N820: "Transit declaration",
    N952: "TIR declaration",
    N955: "ATA carnet",
    N990: "Customs declaration",
  };

  const [mainTransportDocuments, setMainTransportDocuments] = useState([]);

  // Keep address mode with each house party, including when switching houses.
  const addressFlagConsignor = selectedConsignment?.houses?.[selectedHouseIndex]?.consignor?.addressFlagConsignor
    ?? !selectedConsignment?.houses?.[selectedHouseIndex]?.consignor?.poBoxNumber?.trim();
  const setAddressFlagConsignor = (value) => setSelectedHouse((house) => ({
    ...house,
    consignor: { ...house.consignor, addressFlagConsignor: value },
  }));
  const addressFlagConsignee = selectedConsignment?.houses?.[selectedHouseIndex]?.consignee?.addressFlagConsignee
    ?? !selectedConsignment?.houses?.[selectedHouseIndex]?.consignee?.poBoxNumber?.trim();
  const setAddressFlagConsignee = (value) => setSelectedHouse((house) => ({
    ...house,
    consignee: { ...house.consignee, addressFlagConsignee: value },
  }));
  const addressFlagSeller = selectedConsignment?.houses?.[selectedHouseIndex]?.seller?.addressFlagSeller
    ?? !selectedConsignment?.houses?.[selectedHouseIndex]?.seller?.poBoxNumber?.trim();
  const setAddressFlagSeller = (value) => setSelectedHouse((house) => ({
    ...house,
    seller: { ...house.seller, addressFlagSeller: value },
  }));
  const addressFlagBuyer = selectedConsignment?.houses?.[selectedHouseIndex]?.buyer?.addressFlagBuyer
    ?? !selectedConsignment?.houses?.[selectedHouseIndex]?.buyer?.poBoxNumber?.trim();
  const setAddressFlagBuyer = (value) => setSelectedHouse((house) => ({
    ...house,
    buyer: { ...house.buyer, addressFlagBuyer: value },
  }));

  const [countries, setCountries] = useState([]);
  const [countriesSearchC1, setCountriesSearchC1] = useState("");
  const [countriesSearchS, setCountriesSearchS] = useState("");
  const [countriesSearchB, setCountriesSearchB] = useState("");
  const [countriesSearchC2, setCountriesSearchC2] = useState("");
  const [countriesSearchPlateNationality, setCountriesSearchPlateNationality] =
    useState("");

  const [countriesSearchPlL, setCountriesSearchPlL] = useState("");
  const [countriesSearchPlUL, setCountriesSearchPlUL] = useState("");
  const [countriesSearchPlA, setCountriesSearchPlA] = useState("");
  const [countriesSearchPlD, setCountriesSearchPlD] = useState("");
  const [countriesSearchHPlA, setCountriesSearchHPlA] = useState("");
  const [countriesSearchHPlD, setCountriesSearchHPlD] = useState("");

  const [citiesConsignor, setCitiesConsignor] = useState([]);
  const [citiesConsignee, setCitiesConsignee] = useState([]);
  const [citiesSeller, setCitiesSeller] = useState([]);
  const [citiesBuyer, setCitiesBuyer] = useState([]);
  const [citiesSearchConsignor, setCitiesSearchConsignor] = useState("");
  const [citiesSearchConsignee, setCitiesSearchConsignee] = useState("");
  const [citiesSearchSeller, setCitiesSearchSeller] = useState("");
  const [citiesSearchBuyer, setCitiesSearchBuyer] = useState("");

  //places cities
  const [citiesPlOfLoading, setCitiesPlOfLoading] = useState([]);
  const [citiesPlOfUnLoading, setCitiesPlOfUnLoading] = useState([]);
  const [citiesPlOfAcceptance, setCitiesPlOfAcceptance] = useState([]);
  const [citiesPlOfDelivery, setCitiesPlOfDelivery] = useState([]);
  const [citiesHPlOfAcceptance, setCitiesHPlOfAcceptance] = useState([]);
  const [citiesHPlOfDelivery, setCitiesHPlOfDelivery] = useState([]);

  const [citiesSearchPlOfLoading, setCitiesSearchPlOfLoading] = useState("");
  const [citiesSearchPlOfUnloading, setCitiesSearchPlOfUnloading] =
    useState("");
  const [citiesSearchPlOfAcceptance, setCitiesSearchPlOfAcceptance] =
    useState("");
  const [citiesSearchPlOfDelivery, setCitiesSearchPlOfDelivery] = useState("");
  const [citiesSearchHPlOfAcceptance, setCitiesSearchHPlOfAcceptance] =
    useState("");
  const [citiesSearchHPlOfDelivery, setCitiesSearchHPlOfDelivery] =
    useState("");

  const [packageTypes, setPackageTypes] = useState([]);
  const [customsOffices, setCustomsOffices] = useState([]);
  const [customsOfficeSearchS, setCustomsOfficeSearchS] = useState("");

  const [loading, setLoading] = useState(false);
  const [formDataErrors, setFormDataErrors] = useState({});
  const [organizationSearch, setOrganizationSearch] = useState("");
  const [clientOrganizations, setClientOrganizations] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [carrierSearch, setCarrierSearch] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [openAddNewCarrierDialog, setOpenAddNewCarrierDialog] = useState(false);

  const [isDirty, setIsDirty] = useState(false);

  const skipInitialItineraryEffect = useRef(true);

    const [alerts, setAlerts] = useState([]);
  

  const sanitize = (v) =>
    v?.replace(
      /[\u00A0\u2007\u202F\u00A0\u1680\u180E\u2000-\u200D\u202F\u205F\u2060\u3000\uFEFF]/g,
      " ",
    );

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [availableShips, setAvailableShips] = useState([]);

  const selectedHouse =
    selectedConsignment?.houses?.[selectedHouseIndex] ?? null;

  // error helpers (prefer new path; fall back to legacy path)
  const NO_ROOT_FALLBACK = new Set(["mrnReference"]);
  const fieldError = (path) => getValidationError(formDataErrors, path);

  const err = (path) => {
    const housePath = `houses[${selectedHouseIndex}].${path}`;

    const houseError = fieldError(housePath);
    if (houseError !== undefined) {
      return houseError;
    }

    if (NO_ROOT_FALLBACK.has(path)) {
      return undefined;
    }

    return fieldError(path);
  };

  const goodsErr = (rowIndex, field) =>
    fieldError(`houses[${selectedHouseIndex}].goodsList[${rowIndex}].${field}`) ?? fieldError(`goodsList[${rowIndex}].${field}`);

  useEffect(() => {
    skipInitialItineraryEffect.current = true;
    getAllMainTransportDocuments();
    getAllCountries();
    getAllPackageTypes();
    getAllCustomsOffices();

    if (data?.consignments?.length > 0) {
      fetchConsignment(data?.consignments[0]?.id);
    } else {
      const newC = normalizeConsignment({
        consignmentNumber: generateFourDigitNumber(),
        organization: { ...data?.organization },
        approxArrivalDateAndTime: moment()
          .add(1, "days")
          .startOf("day")
          .format(),
        accompanied: true,
      });
      setConsignments([newC]);
      setSelectedConsignment(newC);
      setSelectedHouseIndex(0);
    }
  }, []);

  //IMO REQUIRED - load available ships when customs office changes
  useEffect(() => {
    if (selectedConsignment?.customsOffice?.imoRequired) {
      loadAvailableShips(selectedConsignment?.customsOffice?.id);
    }
  }, [selectedConsignment?.customsOffice]);

  const loadAvailableShips = (customsOfficeId) => {
    if (!customsOfficeId) {
      setAvailableShips([]);
      return;
    }
    ShipsCodebookRepository.getAllByCustomsOfficeId(customsOfficeId)
      .then((response) => {
        setAvailableShips(response.data);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const [loadConsignment, setLoadConsignment] = useState(false);

  const fetchConsignment = (id) => {
    skipInitialItineraryEffect.current = true;
    setLoadConsignment(true);
    ConsignmentRepository.getConsignmentById(id)
      .then((response) => {
        if (!response?.data?.id) {
          console.log("Declaration not found");
          navigate(window.location.pathname.replace(/\/[^/]*$/, ""));
        }
        const cons = normalizeConsignment(response.data);
        getAllConsignmentsForTicket(cons);
      })
      .catch((err) => {
        console.log(err);
        navigate(window.location.pathname.replace(/\/[^/]*$/, ""));
        setLoadConsignment(false);
      });
  };

  const getAllConsignmentsForTicket = (consignment) => {
    ConsignmentRepository.getByTicket(consignment?.ticketId)
      .then((response) => {
        let tmpConsignments = response.data.map(normalizeConsignment);

        tmpConsignments = [
          ...tmpConsignments.filter((c) => c.id === consignment.id),
          ...tmpConsignments.filter((c) => c.id !== consignment.id),
        ];

        setConsignments(tmpConsignments);
        setSelectedConsignment(normalizeConsignment(consignment));
        setSelectedHouseIndex(0);
        setLoadConsignment(false);
      })
      .catch((err) => {
        console.log(err);
        dispatch(notifyShowErrorMessage(ErrorMessageResolver.resolve(err)));
      });
  };

  // Organizations lookup
  useEffect(() => {
    if (organizationSearch?.length >= 3) {
      OrganizationsRepository.getBySearchString(organizationSearch)
        .then((res) => {
          setClientOrganizations(
            res.data.filter((org) => org.type === "CLIENT"),
          );
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [organizationSearch]);

  // Cities load by selected house country changes
  useEffect(() => {
    if (selectedHouse?.seller?.country?.code) {
      getAllCitiesByCountry(
        selectedHouse?.seller?.country?.code,
        setCitiesSeller,
      );
    }
  }, [selectedHouse?.seller?.country?.code]);

  useEffect(() => {
    if (selectedHouse?.buyer?.country?.code) {
      getAllCitiesByCountry(
        selectedHouse?.buyer?.country?.code,
        setCitiesBuyer,
      );
    }
  }, [selectedHouse?.buyer?.country?.code]);

  useEffect(() => {
    if (selectedConsignment?.organization?.id) {
      getAllCarriers();
      getAllDeclarants();
    }
  }, [selectedConsignment?.organization?.id]);

  useEffect(() => {
    if (selectedHouse?.consignor?.country?.code) {
      getAllCitiesByCountry(
        selectedHouse?.consignor?.country?.code,
        setCitiesConsignor,
      );
    }
  }, [selectedHouse?.consignor?.country?.code]);

  useEffect(() => {
    if (selectedHouse?.consignee?.country?.code) {
      getAllCitiesByCountry(
        selectedHouse?.consignee?.country?.code,
        setCitiesConsignee,
      );
    }
  }, [selectedHouse?.consignee?.country?.code]);

  // Cities load by selected place change
  useEffect(() => {
    if (selectedConsignment?.placeOfLoading?.countryCode) {
      getAllCitiesByCountry(
        selectedConsignment?.placeOfLoading?.countryCode,
        setCitiesPlOfLoading,
      );
    }
  }, [selectedConsignment?.placeOfLoading?.countryCode]);

  useEffect(() => {
    if (selectedConsignment?.placeOfUnloading?.countryCode) {
      getAllCitiesByCountry(
        selectedConsignment?.placeOfUnloading?.countryCode,
        setCitiesPlOfUnLoading,
      );
    }
  }, [selectedConsignment?.placeOfUnloading?.countryCode]);

  useEffect(() => {
    if (selectedConsignment?.placeOfAcceptance?.countryCode) {
      getAllCitiesByCountry(
        selectedConsignment?.placeOfAcceptance?.countryCode,
        setCitiesPlOfAcceptance,
      );
    }
  }, [selectedConsignment?.placeOfAcceptance?.countryCode]);

  useEffect(() => {
    if (selectedConsignment?.placeOfDelivery?.countryCode) {
      getAllCitiesByCountry(
        selectedConsignment?.placeOfDelivery?.countryCode,
        setCitiesPlOfDelivery,
      );
    }
  }, [selectedConsignment?.placeOfDelivery?.countryCode]);

  useEffect(() => {
    if (selectedHouse?.placeOfAcceptance?.countryCode) {
      getAllCitiesByCountry(
        selectedHouse?.placeOfAcceptance?.countryCode,
        setCitiesHPlOfAcceptance,
      );
    }
  }, [selectedHouse?.placeOfAcceptance?.countryCode]);

  useEffect(() => {
    if (selectedHouse?.placeOfDelivery?.countryCode) {
      getAllCitiesByCountry(
        selectedHouse?.placeOfDelivery?.countryCode,
        setCitiesHPlOfDelivery,
      );
    }
  }, [selectedHouse?.placeOfDelivery?.countryCode]);

  const getAllMainTransportDocuments = () => {
    ConsignmentRepository.getConsignmentMainTransportDocuments()
      .then((response) => {
        setMainTransportDocuments(response.data);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const getAllCountries = () => {
    CountriesV2Repository.fetchAllWithoutPaging()
      .then((res) => {
        setCountries(res.data);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const getAllCitiesByCountry = (countryId, setCities) => {
    setLoading(true);
    CountriesV2Repository.getCitiesByCountry(countryId)
      .then((res) => {
        setCities(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  };

  const getAllCarriers = () => {
    if (selectedConsignment?.organization?.id) {
      CarrierRepository.getAllByOrganization(
        selectedConsignment?.organization?.id,
        "CARRIER",
      )
        .then((response) => {
          setCarriers(response.data);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  };

  const [declarants, setDeclarants] = useState([]);
  const [declarantSearch, setDeclarantSearch] = useState("");
  const getAllDeclarants = () => {
    if (selectedConsignment?.organization?.id) {
      CarrierRepository.getAllByOrganization(
        selectedConsignment?.organization?.id,
        "DECLARANT",
      )
        .then((response) => {
          setDeclarants(response.data);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  };

  const getAllPackageTypes = () => {
    ConsignmentRepository.getConsignmentPackageTypes()
      .then((response) => {
        setPackageTypes(response.data);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const getAllCustomsOffices = () => {
    ConsignmentRepository.getCustomsOffices()
      .then((response) => {
        setCustomsOffices(response.data);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  /** -------- House setters -------- */

  const setSelectedHouse = (updater) => {
    setSelectedConsignment((prev) => {
      if (!prev) return prev;
      const houses = [...(prev.houses ?? [])];
      const current = houses[selectedHouseIndex] ?? defaultHouse();
      houses[selectedHouseIndex] =
        typeof updater === "function" ? updater(current) : updater;
      return { ...prev, houses };
    });
  };

  const handleHouseChange = (key, value) => {
    const isPlaceChange =
      key.startsWith("placeOfAcceptance.") ||
      key.startsWith("placeOfDelivery.");

    const isSellerChange = key.startsWith("seller.");
    const isBuyerChange = key.startsWith("buyer.");

    const isConsignorChange = key.startsWith("consignor.");
    const isConsigneeChange = key.startsWith("consignee.");

    setSelectedHouse((prev) => {
      const next = { ...prev };

      /** -----------------------------
       * PLACE (manual edits by user)
       * ----------------------------- */
      if (isPlaceChange) {
        const [placeKey, field] = key.split(".");

        _.set(next, key, value);

        // CITY change
        if (field === "cityName") {
          const cities =
            placeKey === "placeOfAcceptance"
              ? citiesHPlOfAcceptance
              : citiesHPlOfDelivery;

          const selectedCity = cities?.find((c) => c.cityName === value);

          _.set(
            next,
            `${placeKey}.cityNameWoDiacritics`,
            selectedCity ? selectedCity.cityNameWoDiacritics : null,
          );
        }

        // COUNTRY change
        if (field === "countryName") {
          const selectedCountry = countries.find((c) => c.name === value);

          _.set(
            next,
            `${placeKey}.countryCode`,
            selectedCountry ? selectedCountry.code : null,
          );

          // when country change clear city
          // if (!selectedCountry) {
          _.set(next, `${placeKey}.cityName`, null);
          _.set(next, `${placeKey}.cityNameWoDiacritics`, null);
          // }
        }

        return next;
      }

      /** -----------------------------
       * CONSIGNOR / CONSIGNEE (only clear city if country changes)
       * ----------------------------- */
      if (isConsignorChange) {
        _.set(next, key, value);
        if (key === "consignor.country") {
          _.set(next, "consignor.city", null);
        }
      }

      if (isConsigneeChange) {
        _.set(next, key, value);
        if (key === "consignee.country") {
          _.set(next, "consignee.city", null);
        }
      }

      /** -----------------------------
       * SELLER → PLACE OF ACCEPTANCE
       * ----------------------------- */
      if (isSellerChange) {
        _.set(next, key, value);

        if (key === "seller.country") {
          _.set(next, "placeOfAcceptance.countryName", value?.name ?? null);
          _.set(next, "placeOfAcceptance.countryCode", value?.code ?? null);

          // if (!value) {
          _.set(next, "placeOfAcceptance.cityName", null);
          _.set(next, "placeOfAcceptance.cityNameWoDiacritics", null);
          _.set(next, "seller.city", null);
          // }
        }

        if (key === "seller.city") {
          _.set(next, "placeOfAcceptance.cityName", value?.cityName ?? null);
          _.set(
            next,
            "placeOfAcceptance.cityNameWoDiacritics",
            value?.cityNameWoDiacritics ?? null,
          );
        }

        return next;
      }

      /** -----------------------------
       * BUYER → PLACE OF DELIVERY
       * ----------------------------- */
      if (isBuyerChange) {
        _.set(next, key, value);

        if (key === "buyer.country") {
          _.set(next, "placeOfDelivery.countryName", value?.name ?? null);
          _.set(next, "placeOfDelivery.countryCode", value?.code ?? null);

          // if (!value) {
          _.set(next, "placeOfDelivery.cityName", null);
          _.set(next, "placeOfDelivery.cityNameWoDiacritics", null);
          _.set(next, "buyer.city", null);
          // }
        }

        if (key === "buyer.city") {
          _.set(next, "placeOfDelivery.cityName", value?.cityName ?? null);
          _.set(
            next,
            "placeOfDelivery.cityNameWoDiacritics",
            value?.cityNameWoDiacritics ?? null,
          );
        }

        return next;
      }

      /** -----------------------------
       * CONSIGNOR / CONSIGNEE (only clear city if country changes)
       * ----------------------------- */
      if (isConsignorChange) {
        _.set(next, key, value);
        if (key === "consignor.country") {
          _.set(next, "consignor.city", null);
        }
      }

      if (isConsigneeChange) {
        _.set(next, key, value);
        if (key === "consignee.country") {
          _.set(next, "consignee.city", null);
        }
      }

      /** -----------------------------
       * FALLBACK
       * ----------------------------- */
      _.set(next, key, value);
      return next;
    });
  };

  useEffect(() => {
    if (!selectedHouse) return;

    if (skipInitialItineraryEffect.current) {
      skipInitialItineraryEffect.current = false;
      return;
    }

    const sellerCountry = selectedHouse.seller?.country;
    const buyerCountry = selectedHouse.buyer?.country;
    const customsCode = selectedConsignment?.customsOffice?.city?.country?.code;

    const nextItinerary = [];
    const seen = new Set();

    const addCountry = (country, isAuto = false) => {
      if (!country || seen.has(country.code)) return;

      seen.add(country.code);

      nextItinerary.push({
        ...country,
        __auto: isAuto,
      });
    };

    addCountry(sellerCountry, true);

    if (buyerCountry && customsCode && buyerCountry.code !== customsCode) {
      const customsCountry = countries.find((c) => c.code === customsCode);
      addCountry(customsCountry, true);
    }

    addCountry(buyerCountry, true);

    setSelectedHouse((h) => {
      const existing = h.itineraryCountries ?? [];

      const manualCountries = existing.filter((c) => !c.__auto);

      const merged = [...manualCountries, ...nextItinerary];

      const unique = [];
      const seenCodes = new Set();

      merged.forEach((c) => {
        if (!seenCodes.has(c.code)) {
          seenCodes.add(c.code);
          unique.push(c);
        }
      });

      return {
        ...h,
        itineraryCountries: unique,
      };
    });
  }, [
    selectedHouse?.seller?.country?.code,
    selectedHouse?.buyer?.country?.code,
    selectedConsignment?.customsOffice?.city?.country?.code,
  ]);

  const citiesByPlace = {
    placeOfLoading: citiesPlOfLoading,
    placeOfUnloading: citiesPlOfUnLoading,
    placeOfAcceptance: citiesPlOfAcceptance,
    placeOfDelivery: citiesPlOfDelivery,
  };

  /** -------- Consignment-level changes (unchanged logic) -------- */

  const handleChange = (key, value) => {
    const isPlaceChange =
      key.startsWith("placeOfLoading.") ||
      key.startsWith("placeOfUnloading.") ||
      key.startsWith("placeOfAcceptance.") ||
      key.startsWith("placeOfDelivery.");

    if (isPlaceChange) {
      setSelectedConsignment((prev) => {
        const next = { ...prev };
        const [placeKey, field] = key.split(".");

        // Always set the changed field itself
        _.set(next, key, value);

        // CITY change
        if (field === "cityName") {
          const cities = citiesByPlace[placeKey] || [];
          const selectedCity = cities.find((c) => c.cityName === value);

          _.set(
            next,
            `${placeKey}.cityNameWoDiacritics`,
            selectedCity ? selectedCity.cityNameWoDiacritics : null,
          );
        }

        // COUNTRY change
        if (field === "countryName") {
          const selectedCountry = countries.find((c) => c.name === value);

          _.set(
            next,
            `${placeKey}.countryCode`,
            selectedCountry ? selectedCountry.code : null,
          );

          // if country cleared → reset dependent city fields, also on country change clear city
          // if (!selectedCountry) {
          _.set(next, `${placeKey}.cityName`, null);
          _.set(next, `${placeKey}.cityNameWoDiacritics`, null);
          // }
        }

        return next;
      });
    } else {
      // setSelectedConsignment(_.set({ ...selectedConsignment }, key, value));
      setSelectedConsignment((prev) => {
        let updated = _.set({ ...prev }, key, value);

        // If the organization changes, clear carrier and declarant
        if (key === "organization") {
          updated = _.set(updated, "carrier", null);
          updated = _.set(updated, "declarant", null);
        }

        return updated;
      });

      //If the customsOffice changes, clear IMO number
      if (key === "customsOffice") {
        setSelectedConsignment((prev) => _.set({ ...prev }, "imoNumber", null));
      }

      // If the carrier is selected and type is CARRIER_AND_DECLARANT, also set declarant
      if (key === "carrier" && value?.type === "CARRIER_AND_DECLARANT") {
        setSelectedConsignment((prev) =>
          _.set({ ...prev }, "declarant", value),
        );
      }

      // Only check truck and trailer plate numbers
      if (key === "truckPlateNumber" || key === "trailerPlateNumber") {
        if (/\s/.test(value)) {
          setFormDataErrors((prev) => ({
            ...prev,
            [key]: "Plate number should not contain blank spaces",
          }));
        } else {
          setFormDataErrors((prev) => {
            const updated = { ...prev };
            delete updated[key];
            return updated;
          });
        }
      }
    }
  };

  /** -------- Goods handlers (on selected house) -------- */

  const handleAddRow = () => {
    setSelectedHouse((h) => ({
      ...h,
      goodsList: [...(h.goodsList ?? []), defaultGoodsRow()],
    }));
  };

  const handleDeleteRow = (index) => {
    setSelectedHouse((h) => ({
      ...h,
      goodsList: (h.goodsList ?? []).filter((_, i) => i !== index),
    }));
  };

  const handleChangeRows = (index, field, value) => {
    setSelectedHouse((h) => {
      const list = [...(h.goodsList ?? [])];
      list[index] = { ...(list[index] ?? defaultGoodsRow()), [field]: value };
      return { ...h, goodsList: list };
    });
  };

  /** -------- Validation wrapper GB -------- */
  const validateConsignementGB = (data) => {
    if (!ConsignmentValidator.isValidSync(data)) {
      const validationErrors = {};
      (async () => {
        await ConsignmentValidator.validate(data, {
          abortEarly: false,
        }).catch((err) => {
          err.inner.forEach((validationError) => {
            validationErrors[validationError.path] = validationError.message;
          });
          console.log(validationErrors);
          setFormDataErrors(validationErrors);
          // navigate to first house data with errors
          const firstHouseWithError = Object.keys(validationErrors).find(
            (key) => key.startsWith("houses["),
          );
          if (firstHouseWithError) {
            const houseIndex = parseInt(
              firstHouseWithError.split("[")[1].split("]")[0],
            );
            setSelectedHouseIndex(houseIndex);
          }
          setLoading(false);
          return;
        });
      })();
    } else {
      setFormDataErrors({});
    }
  };

  /** -------- Validation wrapper EU -------- */
  const validateConsignementEU = (data) => {
    if (!ConsignmentValidatorEU.isValidSync(data)) {
      const validationErrors = {};
      (async () => {
        await ConsignmentValidatorEU.validate(data, {
          abortEarly: false,
        }).catch((err) => {
          err.inner.forEach((validationError) => {
            validationErrors[validationError.path] = validationError.message;
          });
          console.log(validationErrors);
          setFormDataErrors(validationErrors);
          // navigate to first house data with errors
          const firstHouseWithError = Object.keys(validationErrors).find(
            (key) => key.startsWith("houses["),
          );
          if (firstHouseWithError) {
            const houseIndex = parseInt(
              firstHouseWithError.split("[")[1].split("]")[0],
            );
            setSelectedHouseIndex(houseIndex);
          }
          setLoading(false);
          return;
        });
      })();
    } else {
      setFormDataErrors({});
    }
  };

  /** -------- Save / Approve / Reject -------- */

  const save = () => {
    setLoading(true);

    let tmpSelectedConsignment = { ...selectedConsignment };

    // Date normalization
    tmpSelectedConsignment.approxArrivalDateAndTime = moment(
      tmpSelectedConsignment.approxArrivalDateAndTime,
    )
      .utc(false)
      .format();

    // Optionally store UI address flags into the currently selected house parties
    if (selectedHouse) {
      const h = _.cloneDeep(selectedHouse);
      h.consignor = {
        ...h.consignor,
        addressFlagConsignor: addressFlagConsignor,
      };
      h.consignee = {
        ...h.consignee,
        addressFlagConsignee: addressFlagConsignee,
      };
      h.seller = { ...h.seller, addressFlagSeller: addressFlagSeller };
      h.buyer = { ...h.buyer, addressFlagBuyer: addressFlagBuyer };

      // write back the updated house into tmpSelectedConsignment
      const houses = [...(tmpSelectedConsignment.houses ?? [])];
      houses[selectedHouseIndex] = h;
      tmpSelectedConsignment.houses = houses;
    }

    // Build compatibility payload (houses[] + legacy mirror from house[0])
    let payload = {};

    if (tmpSelectedConsignment?.customsOffice?.direction === "EU") {
      payload = preparePayloadForSaveEU(tmpSelectedConsignment);
      validateConsignementEU(payload);

      if (ConsignmentValidatorEU.isValidSync(payload)) {
        ConsignmentRepository.addConsignmentToTicket(payload, data?.id)
          .then((response) => {
            const normalized = normalizeConsignment(response.data);
            const updatedConsignments = consignments?.map((item) =>
              item.consignmentNumber === selectedConsignment?.consignmentNumber
                ? normalized
                : item,
            );
            setConsignments(updatedConsignments);
            setSelectedConsignment(normalized);
            loadData();
            dispatch(
              notifyShowSuccessMessage("Declaration saved successfully"),
            );
            setLoading(false);
          })
          .catch((err) => {
            console.log(err);
            dispatch(notifyShowErrorMessage(ErrorMessageResolver.resolve(err)));
            setLoading(false);
          });
      } else {
        setLoading(false);
      }
    } else {
      payload = preparePayloadForSaveGB(tmpSelectedConsignment);
      validateConsignementGB(payload);

      if (ConsignmentValidator.isValidSync(payload)) {
        ConsignmentRepository.addConsignmentToTicket(payload, data?.id)
          .then((response) => {
            const normalized = normalizeConsignment(response.data);
            const updatedConsignments = consignments?.map((item) =>
              item.consignmentNumber === selectedConsignment?.consignmentNumber
                ? normalized
                : item,
            );
            setConsignments(updatedConsignments);
            setSelectedConsignment(normalized);
            loadData();
            dispatch(
              notifyShowSuccessMessage("Declaration saved successfully"),
            );
            setLoading(false);
          })
          .catch((err) => {
            console.log(err);
            dispatch(notifyShowErrorMessage(ErrorMessageResolver.resolve(err)));
            setLoading(false);
          });
      } else {
        setLoading(false);
      }
    }
  };

  const saveDraft = () => {
    setLoading(true);
    let tmpSelectedConsignment = { ...selectedConsignment };

    // Date normalization
    tmpSelectedConsignment.approxArrivalDateAndTime = moment(
      tmpSelectedConsignment.approxArrivalDateAndTime,
    )
      .utc(false)
      .format();

    // Optionally store UI address flags into the currently selected house parties
    if (selectedHouse) {
      const h = _.cloneDeep(selectedHouse);
      h.consignor = {
        ...h.consignor,
        addressFlagConsignor: addressFlagConsignor,
      };
      h.consignee = {
        ...h.consignee,
        addressFlagConsignee: addressFlagConsignee,
      };
      h.seller = { ...h.seller, addressFlagSeller: addressFlagSeller };
      h.buyer = { ...h.buyer, addressFlagBuyer: addressFlagBuyer };

      // write back the updated house into tmpSelectedConsignment
      const houses = [...(tmpSelectedConsignment.houses ?? [])];
      houses[selectedHouseIndex] = h;
      tmpSelectedConsignment.houses = houses;
    }

    let payload = {};
    if (tmpSelectedConsignment?.customsOffice?.direction === "EU") {
      payload = preparePayloadForSaveEU(tmpSelectedConsignment);
    } else {
      payload = preparePayloadForSaveGB(tmpSelectedConsignment);
    }

    ConsignmentRepository.addDraftConsignmentToTicket(payload, data?.id)
      .then((response) => {
        const normalized = normalizeConsignment(response.data);
        const updatedConsignments = consignments?.map((item) =>
          item.consignmentNumber === selectedConsignment?.consignmentNumber
            ? normalized
            : item,
        );
        setConsignments(updatedConsignments);
        setSelectedConsignment(normalized);
        loadData();
        dispatch(notifyShowSuccessMessage("Declaration saved successfully"));
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        dispatch(notifyShowErrorMessage(ErrorMessageResolver.resolve(err)));
        setLoading(false);
      });
  };

  const reject = () => {
    setLoading(true);
    ConsignmentRepository.reject(selectedConsignment?.id)
      .then((response) => {
        const updatedConsignments = consignments?.map((item) =>
          item.consignmentNumber === selectedConsignment?.consignmentNumber
            ? normalizeConsignment(response.data)
            : item,
        );
        setConsignments(updatedConsignments);
        setSelectedConsignment(normalizeConsignment(response.data));
        dispatch(notifyShowSuccessMessage("Declaration rejected successfully"));
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        dispatch(notifyShowErrorMessage(ErrorMessageResolver.resolve(err)));
        setLoading(false);
      });
  };

  const handleChipClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (consignment) => {
    if (consignment) {
      const pathSegments = window.location.pathname.split("/");
      pathSegments[pathSegments.length - 1] = consignment.id;
      const newPath = pathSegments.join("/");
      navigate(newPath, { replace: true });
      fetchConsignment(consignment.id);
    }
    setAnchorEl(null);
  };

  const copyConsignorToSeller = () => {
    if (selectedHouse?.consignor) {
      let tmp = { ...selectedHouse?.seller };
      tmp.name = selectedHouse?.consignor?.name;
      tmp.country = selectedHouse?.consignor?.country;
      tmp.city = selectedHouse?.consignor?.city;
      tmp.postCode = selectedHouse?.consignor?.postCode;
      tmp.address = selectedHouse?.consignor?.address;
      tmp.addressNumber = selectedHouse?.consignor?.addressNumber;
      tmp.poBoxNumber = selectedHouse?.consignor?.poBoxNumber;
      handleHouseChange("seller", tmp);
    }
  };

  const copyBuyerToConsignee = () => {
    if (selectedHouse?.buyer) {
      let tmp = { ...selectedHouse?.consignee };
      tmp.name = selectedHouse?.buyer?.name;
      tmp.country = selectedHouse?.buyer?.country;
      tmp.city = selectedHouse?.buyer?.city;
      tmp.postCode = selectedHouse?.buyer?.postCode;
      tmp.address = selectedHouse?.buyer?.address;
      tmp.addressNumber = selectedHouse?.buyer?.addressNumber;
      tmp.poBoxNumber = selectedHouse?.buyer?.poBoxNumber;
      handleHouseChange("consignee", tmp);
    }
  };

  useEffect(() => {
        const fetchAlerts = async () =>{
          try{
            const response = await AlertRepository.getAllActiveByType({type: "DECLARATION"});
            setAlerts(response.data);
          }catch(error){
            console.error("Failed to fetch alerts: ", error);
          }
        };
        fetchAlerts();
      },[]);
      
      const hasErrorAlert = alerts?.some(
      (alert) => alert?.severity === "error"
      );

  const reviewImportedDraft = async (id) => {
    const response = await ConsignmentRepository.getConsignmentById(id);
    const draft = normalizeConsignment(response.data);
    if (draft.status !== "DRAFT" || (draft.ticketId && draft.ticketId !== data?.id)
        || draft.organization?.id !== data?.organization?.id) {
      throw new Error("Choose an unsubmitted draft for this ticket's organization that is not linked to another ticket.");
    }
    const linked = await ConsignmentRepository.addDraftConsignmentToTicket(draft, data.id);
    const normalized = normalizeConsignment(linked.data);
    skipInitialItineraryEffect.current = true;
    setConsignments((previous) => [normalized, ...(previous ?? []).filter((item) => item.id !== normalized.id)]);
    setSelectedConsignment(normalized);
    setSelectedHouseIndex(0);
    setFormDataErrors({});
    loadData();
  };

  /** -------- Render -------- */

  return (
    <>
      {data?.id && data?.organization?.id && <ConsignmentImports client
        fixedOrganization={data.organization} onReviewDraft={reviewImportedDraft} />}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {selectedConsignment?.consignmentIssues?.length > 0 &&
            selectedConsignment?.consignmentIssues.map((issue, index) => (
              <div key={`${issue?.typ}-${index}`}>
                {index === 0 && (
                  <Alert severity="warning">
                    <AlertTitle>Note</AlertTitle>
                    Please check and correct the following issues before saving
                    the declaration.
                  </Alert>
                )}
                <Alert severity="error">
                  <AlertTitle>{issue?.typ}</AlertTitle>
                  {issue?.description}
                  <br></br>
                  <strong>
                    <span style={{ backgroundColor: "#ffdddd" }}>
                      AI Assistant (beta): {issue?.humanText}
                    </span>
                  </strong>
                </Alert>
              </div>
            ))}
            { alerts.map((alert) =>( alert.severity === "error" &&
                                  (<Grid item xs={8} sx={{ marginBottom: 2 }} key={alert.id}>
                                  <Alert severity={alert.severity}>
                                      {alert.description}
                                  </Alert>
                                </Grid>)
                                ))
                                } 
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Ticket Details
          </Typography>
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Ticket Number"
            value={data?.ticketNumber}
            sx={{ backgroundColor: "#f2f2f2" }}
          ></TextField>
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Destination Route"
            value={data?.productDestination?.destination?.name}
            sx={{ backgroundColor: "#f2f2f2" }}
          ></TextField>
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Departure"
            value={
              moment(data?.departureDay).format("DD/MM/yyyy") +
              " " +
              data?.departureHour
            }
            sx={{ backgroundColor: "#f2f2f2" }}
          ></TextField>
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Plate Number"
            value={data?.truckPlateNumber}
            sx={{ backgroundColor: "#f2f2f2" }}
          ></TextField>
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Reservation Code"
            value={data?.reservationCode}
            sx={{ backgroundColor: "#f2f2f2" }}
          ></TextField>
        </Grid>

        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Supplier"
            value={data?.productDestination?.supplier?.name}
            sx={{ backgroundColor: "#f2f2f2" }}
          ></TextField>
        </Grid>
        <Grid item xs={12} md={1.5}></Grid>

        {/* Section: General Details */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            General Details
          </Typography>
        </Grid>

        {/* Client Information */}
        <Grid item xs={12} md={4}>
          <Autocomplete
            noOptionsText={LOCALE.noOptionsText}
            autoComplete={false}
            size="small"
            value={
              selectedConsignment?.organization?.nameEn
                ? `${selectedConsignment?.organization?.nameEn}`
                : ""
            }
            disabled
            inputValue={organizationSearch ? organizationSearch : ""}
            onInputChange={(event, newInputValue) => {
              setOrganizationSearch(newInputValue);
            }}
            options={clientOrganizations?.map(
              (organization) => `${organization.nameEn}`,
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={LOCALE.organization}
                fullWidth
                disabled
                variant="outlined"
                error={fieldError(`organization.name`) !== undefined}
                helperText={fieldError(`organization.name`)}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Autocomplete
              noOptionsText={LOCALE.noOptionsText}
              autoComplete={false}
              size="small"
              value={
                selectedConsignment?.carrier?.name
                  ? `${selectedConsignment?.carrier?.name} (${selectedConsignment?.carrier?.eori})`
                  : ""
              }
              onChange={(event, newValue) => {
                handleChange(
                  "carrier",
                  carriers?.filter(
                    (carried) =>
                      `${carried.name} (${carried?.eori})` === newValue,
                  )[0],
                );
                setIsDirty(true);
              }}
              inputValue={carrierSearch ? carrierSearch : ""}
              onInputChange={(event, newInputValue) => {
                setCarrierSearch(newInputValue);
              }}
              options={carriers?.map(
                (carried) => `${carried.name} (${carried?.eori})`,
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={"Carrier"}
                  fullWidth
                  variant="outlined"
                  error={fieldError(`carrier.name`) !== undefined}
                  helperText={
                    fieldError(`carrier.name`)
                      ? fieldError(`carrier.name`)
                      : "Company responsible for transporting the goods."
                  }
                />
              )}
              sx={{ flexGrow: 1 }} // Autocomplete takes all available width
              disabled={selectedConsignment?.organization?.id === undefined}
            />

            {/* <Tooltip title={"Add new carrier"} arrow placement="top">
                         <IconButton
                           onClick={() => {
                             setOpenAddNewCarrierDialog(true);
                           }}
                           sx={{ ml: 1 }}
                           disabled={
                             selectedConsignment?.organization?.id === undefined
                           }
                         >
                           <AddCircleOutline />
                         </IconButton>
                       </Tooltip> */}
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 0.5,
            }}
          >
            <Autocomplete
              noOptionsText={LOCALE.noOptionsText}
              autoComplete={false}
              size="small"
              value={
                selectedConsignment?.declarant?.name
                  ? `${selectedConsignment?.declarant?.name} (${selectedConsignment?.declarant?.eori})`
                  : ""
              }
              onChange={(event, newValue) => {
                handleChange(
                  "declarant",
                  declarants?.filter(
                    (declarant) =>
                      `${declarant.name} (${declarant?.eori})` === newValue,
                  )[0],
                );
                setIsDirty(true);
              }}
              inputValue={declarantSearch ? declarantSearch : ""}
              onInputChange={(event, newInputValue) => {
                setDeclarantSearch(newInputValue);
              }}
              options={declarants?.map(
                (declarant) => `${declarant.name} (${declarant?.eori})`,
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={LOCALE.declarant}
                  fullWidth
                  variant="outlined"
                  error={fieldError(`declarant.name`) !== undefined}
                  helperText={
                    fieldError(`declarant.name`)
                      ? fieldError(`declarant.name`)
                      : "The person or company submitting the customs or security declaration on behalf of the consignor."
                  }
                />
              )}
              sx={{ flexGrow: 1 }} // Autocomplete takes all available width
              disabled={selectedConsignment?.organization?.id === undefined}
            />

            <Tooltip
              title={"Add new carrier or declarant"}
              arrow
              placement="top"
            >
              <IconButton
                onClick={() => {
                  setOpenAddNewCarrierDialog(true);
                }}
                sx={{ ml: 0.5, verticalAlign: "middle" }}
                disabled={selectedConsignment?.organization?.id === undefined}
              >
                <AddCircleOutline />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Autocomplete
            noOptionsText={LOCALE.noOptionsText}
            autoComplete={false}
            size="small"
            value={
              selectedConsignment?.customsOffice?.id
                ? customsOffices?.find(
                    (office) =>
                      office.id === selectedConsignment?.customsOffice?.id,
                  )
                : null
            }
            onChange={(event, newValue) => {
              handleChange("customsOffice", newValue);
              setIsDirty(true);
            }}
            inputValue={customsOfficeSearchS || ""}
            onInputChange={(event, newInputValue) => {
              setCustomsOfficeSearchS(newInputValue);
            }}
            options={customsOffices}
            getOptionLabel={(option) => option.name}
            getOptionDisabled={(option) => option?.enabled === false}
            renderOption={(props, option) => (
              <li {...props}>
                <div>{`${option.name} (${option.code})`}</div>
              </li>
            )}
            disabled={selectedConsignment?.status === "DONE"}
            renderInput={(params) => (
              <TextField
                {...params}
                label={"Customs office of first entry"}
                fullWidth
                variant="outlined"
                error={fieldError(`customsOffice.id`) !== undefined}
                helperText={
                  fieldError(`customsOffice.id`)
                    ? fieldError(`customsOffice.id`)
                    : "The customs office where the goods first enter the customs territory when submitting an ICS/S&S declaration."
                }
              />
            )}
          />
        </Grid>

        {/* Estimated Arrival Date */}
        <Grid item xs={12} md={4}>
          <LocalizationProvider
            dateAdapter={AdapterMoment}
            adapterLocale="en-gb"
          >
            <DateTimePicker
              renderInput={(props) => <TextField {...props} size="small" />}
              label="Approx. Estimated Arrival Date and Time"
              size="small"
              value={
                selectedConsignment?.approxArrivalDateAndTime
                  ? moment(selectedConsignment?.approxArrivalDateAndTime)
                  : null
              }
              onChange={(newValue) => {
                setSelectedConsignment({
                  ...selectedConsignment,
                  approxArrivalDateAndTime: newValue,
                });
                setIsDirty(true);
              }}
              format="DD.MM.YYYY HH:mm"
              minDate={moment()}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  error:
                    fieldError(`approxArrivalDateAndTime`) !== undefined,
                  helperText: fieldError(`approxArrivalDateAndTime`),
                },
              }}
              ampm={false}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={0} md={4}></Grid>
        {selectedConsignment?.customsOffice?.direction == "EU" && (
          <Grid item xs={12}>
            <Divider />
          </Grid>
        )}

        {/*ICS2 NEW FIELDS PLACES(PLACE OF LOADING/UNLOADING, PLACE OF ACCEPTANCE/DELIVERY)*/}
        {selectedConsignment?.customsOffice?.direction === "EU" && (
          <>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                Master document reference
              </Typography>
              <br></br>
              <Grid container spacing={2}>
                <Grid item xs={12} md={10}>
                  <TextField
                    label="Master document reference"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={selectedConsignment?.mrnReference ?? ""}
                    onChange={(event) => {
                      handleChange("mrnReference", event.target.value);
                      setIsDirty(true);
                    }}
                    disabled={
                      selectedConsignment?.status !== "DRAFT" &&
                      selectedConsignment?.id != null
                    }
                    error={formDataErrors?.mrnReference !== undefined}
                    helperText={
                      formDataErrors?.mrnReference
                        ? formDataErrors?.mrnReference
                        : "Enter the number of the Master document (e.g., Master B/L straight B/L) if available, or using your House shipment number."
                    }
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={8}>
              {" "}
              <Typography variant="h6" gutterBottom>
                Places
              </Typography>
              <br></br>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                    <Grid container spacing={2}>
                      {/* Place of Loading */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, mb: 1, display: "block" }}
                        >
                          Place of Loading
                          <Tooltip
                            arrow
                            placement="top"
                            title={
                              "The location where the goods are physically loaded onto the transport vehicle"
                            }
                          >
                            <Icon
                              fontSize="small"
                              color="action"
                              sx={{ ml: 0.5, verticalAlign: "middle" }}
                            >
                              help_outline
                            </Icon>
                          </Tooltip>
                        </Typography>

                        <Grid container spacing={1}>
                          <Grid item xs={12}>
                            <Autocomplete
                              noOptionsText={LOCALE.noOptionsText}
                              autoComplete={false}
                              size="small"
                              value={
                                selectedConsignment?.placeOfLoading
                                  ?.countryName || ""
                              }
                              onChange={(event, newValue) => {
                                handleChange(
                                  "placeOfLoading.countryName",
                                  countries?.filter(
                                    (country) => country.name === newValue,
                                  )[0]?.name,
                                );
                                setIsDirty(true);
                              }}
                              inputValue={countriesSearchPlL || ""}
                              onInputChange={(event, newInputValue) => {
                                setCountriesSearchPlL(newInputValue);
                              }}
                              options={countries?.map(
                                (country) => country.name,
                              )}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={LOCALE.country + " Loading"}
                                  fullWidth
                                  variant="outlined"
                                  error={
                                    fieldError(`placeOfLoading.countryName`) !== undefined
                                  }
                                  helperText={
                                    fieldError(`placeOfLoading.countryName`)
                                  }
                                />
                              )}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <Autocomplete
                              noOptionsText={LOCALE.noOptionsText}
                              autoComplete={false}
                              size="small"
                              value={
                                selectedConsignment?.placeOfLoading?.cityName ||
                                ""
                              }
                              onChange={(event, newValue) => {
                                handleChange(
                                  "placeOfLoading.cityName",
                                  citiesPlOfLoading?.filter(
                                    (item) => item.cityName === newValue,
                                  )[0]?.cityName,
                                );
                                setIsDirty(true);
                              }}
                              inputValue={citiesSearchPlOfLoading || ""}
                              onInputChange={(event, newInputValue) => {
                                setCitiesSearchPlOfLoading(newInputValue);
                              }}
                              options={citiesPlOfLoading?.map(
                                (item) => item.cityName,
                              )}
                              fullWidth
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={LOCALE.city + " Loading"}
                                  variant="outlined"
                                  size="small"
                                  error={
                                    fieldError(`placeOfLoading.cityName`) !== undefined
                                  }
                                  helperText={
                                    fieldError(`placeOfLoading.cityName`)
                                  }
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Place of Unloading */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, mb: 1, display: "block" }}
                        >
                          Place of Unloading
                          <Tooltip
                            arrow
                            placement="top"
                            title={
                              "The location where the goods are physically unloaded from the transport vehicle."
                            }
                          >
                            <Icon
                              fontSize="small"
                              color="action"
                              sx={{ ml: 0.5, verticalAlign: "middle" }}
                            >
                              help_outline
                            </Icon>
                          </Tooltip>
                        </Typography>

                        <Grid container spacing={1}>
                          <Grid item xs={12}>
                            <Autocomplete
                              noOptionsText={LOCALE.noOptionsText}
                              autoComplete={false}
                              size="small"
                              value={
                                selectedConsignment?.placeOfUnloading
                                  ?.countryName || ""
                              }
                              onChange={(event, newValue) => {
                                handleChange(
                                  "placeOfUnloading.countryName",
                                  countries?.filter(
                                    (country) => country.name === newValue,
                                  )[0]?.name,
                                );
                                setIsDirty(true);
                              }}
                              inputValue={countriesSearchPlUL || ""}
                              onInputChange={(event, newInputValue) => {
                                setCountriesSearchPlUL(newInputValue);
                              }}
                              options={countries?.map(
                                (country) => country.name,
                              )}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={LOCALE.country + " Unloading"}
                                  fullWidth
                                  variant="outlined"
                                  error={
                                    fieldError(`placeOfUnloading.countryName`) !== undefined
                                  }
                                  helperText={
                                    fieldError(`placeOfUnloading.countryName`)
                                  }
                                />
                              )}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <Autocomplete
                              noOptionsText={LOCALE.noOptionsText}
                              autoComplete={false}
                              size="small"
                              value={
                                selectedConsignment?.placeOfUnloading
                                  ?.cityName || ""
                              }
                              onChange={(event, newValue) => {
                                handleChange(
                                  "placeOfUnloading.cityName",
                                  citiesPlOfUnLoading?.filter(
                                    (item) => item.cityName === newValue,
                                  )[0]?.cityName,
                                );
                                setIsDirty(true);
                              }}
                              inputValue={citiesSearchPlOfUnloading || ""}
                              onInputChange={(event, newInputValue) => {
                                setCitiesSearchPlOfUnloading(newInputValue);
                              }}
                              options={citiesPlOfUnLoading?.map(
                                (item) => item.cityName,
                              )}
                              fullWidth
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={LOCALE.city + " Unloading"}
                                  variant="outlined"
                                  size="small"
                                  error={
                                    fieldError(`placeOfUnloading.cityName`) !== undefined
                                  }
                                  helperText={
                                    fieldError(`placeOfUnloading.cityName`)
                                  }
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                    <Grid container spacing={2}>
                      {/* Place of Acceptance */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, mb: 1, display: "block" }}
                        >
                          Place of Acceptance
                          <Tooltip
                            arrow
                            placement="top"
                            title={
                              "The location where the carrier takes over the goods from the shipper for transportation."
                            }
                          >
                            <Icon
                              fontSize="small"
                              color="action"
                              sx={{ ml: 0.5, verticalAlign: "middle" }}
                            >
                              help_outline
                            </Icon>
                          </Tooltip>
                        </Typography>

                        <Grid container spacing={1}>
                          <Grid item xs={12}>
                            <Autocomplete
                              noOptionsText={LOCALE.noOptionsText}
                              autoComplete={false}
                              size="small"
                              value={
                                selectedConsignment?.placeOfAcceptance
                                  ?.countryName || ""
                              }
                              onChange={(event, newValue) => {
                                handleChange(
                                  "placeOfAcceptance.countryName",
                                  countries?.filter(
                                    (country) => country.name === newValue,
                                  )[0]?.name,
                                );
                                setIsDirty(true);
                              }}
                              inputValue={countriesSearchPlA || ""}
                              onInputChange={(event, newInputValue) => {
                                setCountriesSearchPlA(newInputValue);
                              }}
                              options={countries?.map(
                                (country) => country.name,
                              )}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={LOCALE.country + " Acceptance"}
                                  fullWidth
                                  variant="outlined"
                                  error={
                                    fieldError(`placeOfAcceptance.countryName`) !== undefined
                                  }
                                  helperText={
                                    fieldError(`placeOfAcceptance.countryName`)
                                  }
                                />
                              )}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <Autocomplete
                              noOptionsText={LOCALE.noOptionsText}
                              autoComplete={false}
                              size="small"
                              value={
                                selectedConsignment?.placeOfAcceptance
                                  ?.cityName || ""
                              }
                              onChange={(event, newValue) => {
                                handleChange(
                                  "placeOfAcceptance.cityName",
                                  citiesPlOfAcceptance?.filter(
                                    (item) => item.cityName === newValue,
                                  )[0]?.cityName,
                                );
                                setIsDirty(true);
                              }}
                              inputValue={citiesSearchPlOfAcceptance || ""}
                              onInputChange={(event, newInputValue) => {
                                setCitiesSearchPlOfAcceptance(newInputValue);
                              }}
                              options={citiesPlOfAcceptance?.map(
                                (item) => item.cityName,
                              )}
                              fullWidth
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={LOCALE.city + " Acceptance"}
                                  variant="outlined"
                                  size="small"
                                  error={
                                    fieldError(`placeOfAcceptance.cityName`) !== undefined
                                  }
                                  helperText={
                                    fieldError(`placeOfAcceptance.cityName`)
                                  }
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Place of Delivery */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, mb: 1, display: "block" }}
                        >
                          Place of Delivery
                          <Tooltip
                            arrow
                            placement="top"
                            title={
                              "The location where the goods are handed over to the consignee at the end of the transport."
                            }
                          >
                            <Icon
                              fontSize="small"
                              color="action"
                              sx={{ ml: 0.5, verticalAlign: "middle" }}
                            >
                              help_outline
                            </Icon>
                          </Tooltip>
                        </Typography>

                        <Grid container spacing={1}>
                          <Grid item xs={12}>
                            <Autocomplete
                              noOptionsText={LOCALE.noOptionsText}
                              autoComplete={false}
                              size="small"
                              value={
                                selectedConsignment?.placeOfDelivery
                                  ?.countryName || ""
                              }
                              onChange={(event, newValue) => {
                                handleChange(
                                  "placeOfDelivery.countryName",
                                  countries?.filter(
                                    (country) => country.name === newValue,
                                  )[0]?.name,
                                );
                                setIsDirty(true);
                              }}
                              inputValue={countriesSearchPlD || ""}
                              onInputChange={(event, newInputValue) => {
                                setCountriesSearchPlD(newInputValue);
                              }}
                              options={countries?.map(
                                (country) => country.name,
                              )}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={LOCALE.country + " Delivery"}
                                  fullWidth
                                  variant="outlined"
                                  error={
                                    fieldError(`placeOfDelivery.countryName`) !== undefined
                                  }
                                  helperText={
                                    fieldError(`placeOfDelivery.countryName`)
                                  }
                                />
                              )}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <Autocomplete
                              noOptionsText={LOCALE.noOptionsText}
                              autoComplete={false}
                              size="small"
                              value={
                                selectedConsignment?.placeOfDelivery
                                  ?.cityName || ""
                              }
                              onChange={(event, newValue) => {
                                handleChange(
                                  "placeOfDelivery.cityName",
                                  citiesPlOfDelivery?.filter(
                                    (item) => item.cityName === newValue,
                                  )[0]?.cityName,
                                );
                                setIsDirty(true);
                              }}
                              inputValue={citiesSearchPlOfDelivery || ""}
                              onInputChange={(event, newInputValue) => {
                                setCitiesSearchPlOfDelivery(newInputValue);
                              }}
                              options={citiesPlOfDelivery?.map(
                                (item) => item.cityName,
                              )}
                              fullWidth
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={LOCALE.city + " Delivery"}
                                  variant="outlined"
                                  size="small"
                                  error={
                                    fieldError(`placeOfDelivery.cityName`) !== undefined
                                  }
                                  helperText={
                                    fieldError(`placeOfDelivery.cityName`)
                                  }
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* Section: Vehicle Details */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Vehicle Details
          </Typography>
        </Grid>

        {/* Plate Nationality */}
        <Grid item xs={12} md={2}>
          <Autocomplete
            noOptionsText={LOCALE.noOptionsText}
            autoComplete={false}
            size="small"
            value={
              selectedConsignment?.truckPlateNationality?.name
                ? selectedConsignment?.truckPlateNationality?.name
                : ""
            }
            onChange={(event, newValue) => {
              handleChange(
                "truckPlateNationality",
                countries?.filter((country) => country.name === newValue)[0],
              );
              setIsDirty(true);
            }}
            inputValue={
              countriesSearchPlateNationality
                ? countriesSearchPlateNationality
                : ""
            }
            onInputChange={(event, newInputValue) => {
              setCountriesSearchPlateNationality(newInputValue);
            }}
            options={countries?.map((country) => country.name)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={"Nationality (of the registration plate)"}
                fullWidth
                variant="outlined"
                error={
                  fieldError(`truckPlateNationality.name`) !== undefined
                }
                helperText={
                  fieldError(`truckPlateNationality.name`)
                    ? fieldError(`truckPlateNationality.name`)
                    : "The country code of the vehicle’s registration plate."
                }
              />
            )}
          />
        </Grid>
        {/* Truck Plate Number */}
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Vehicle Reg Num"
            value={selectedConsignment?.truckPlateNumber || ""}
            onChange={(event) => {
              handleChange("truckPlateNumber", event.target.value);

              setIsDirty(true);
            }}
            error={fieldError(`truckPlateNumber`) !== undefined}
            helperText={
              fieldError(`truckPlateNumber`)
                ? fieldError(`truckPlateNumber`)
                : "The registration number of the transport vehicle."
            }
          />
        </Grid>

        {/* Trailer Plate Number */}
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Trailer Reg Num"
            value={selectedConsignment?.trailerPlateNumber || ""}
            onChange={(event) => {
              handleChange("trailerPlateNumber", event.target.value);
              setIsDirty(true);
            }}
            error={fieldError(`trailerPlateNumber`) !== undefined}
            helperText={
              fieldError(`trailerPlateNumber`)
                ? fieldError(`trailerPlateNumber`)
                : "The registration number of the trailer (if applicable)."
            }
          />
        </Grid>
        <Grid item xs={12} md={3}>
          {selectedConsignment?.customsOffice?.imoRequired ? (
            <Autocomplete
              autoComplete={false}
              size="small"
              value={
                selectedConsignment?.imoNumber
                  ? availableShips?.find(
                      (ship) =>
                        ship.imoNumber === selectedConsignment.imoNumber,
                    ) || null
                  : null
              }
              onChange={(event, newValue) => {
                handleChange("imoNumber", newValue ? newValue.imoNumber : "");
                setIsDirty(true);
              }}
              inputValue={selectedConsignment?.imoNumber || ""}
              onInputChange={(event, newInputValue, reason) => {
                // Only update input for free typing if you want
                // Otherwise, ignore to keep it synced with selected IMO
              }}
              noOptionsText="No ships available"
              options={availableShips || []}
              getOptionLabel={(option) =>
                `${option.name} - ${option.imoNumber}`
              }
              renderOption={(props, option) => (
                <li {...props}>{`${option.name} - ${option.imoNumber}`}</li>
              )}
              disabled={selectedConsignment?.status === "DONE"}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="IMO Number"
                  fullWidth
                  size="small"
                  error={fieldError(`imoNumber`) !== undefined}
                  helperText={
                    fieldError(`imoNumber`)
                      ? fieldError(`imoNumber`)
                      : "Unique identification number ship number by the International Maritime Organization."
                  }
                />
              )}
            />
          ) : (
            <TextField
              fullWidth
              size="small"
              label="IMO Number"
              value={selectedConsignment?.imoNumber || ""}
              helperText={
                fieldError(`imoNumber`)
                  ? fieldError(`imoNumber`)
                  : "Unique identification number ship number by the International Maritime Organization."
              }
              onChange={(event) => {
                handleChange("imoNumber", event.target.value);
                setIsDirty(true);
              }}
            />
          )}
        </Grid>
        <Grid item xs={12} md={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={
                  selectedConsignment?.accompanied
                    ? selectedConsignment?.accompanied
                    : false
                }
                onChange={(event) => {
                  handleChange("accompanied", event.target.checked);
                  setIsDirty(true);
                }}
              />
            }
            label={"Accompanied"}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={2}>
            {/* House selector */}
            <Typography variant="h6" gutterBottom>
              House details
            </Typography>
            {selectedConsignment?.houses?.length > 0 && (
              <Tooltip title="Houses in this declaration" placement="top" arrow>
                <div style={{ padding: 5 }}>
                  <Chip
                    label={`House ${selectedHouseIndex + 1} of ${
                      selectedConsignment.houses.length
                    }  ${
                      selectedConsignment?.houses[selectedHouseIndex]
                        ?.mrnReference ?? ""
                    }`}
                    color="secondary"
                    onClick={(e) => setAnchorElHouse(e.currentTarget)}
                    variant="outlined"
                    icon={<Icon>keyboard_arrow_down</Icon>}
                    disabled={
                      !selectedConsignment || loading || loadConsignment
                    }
                  />
                  <Menu
                    anchorEl={anchorElHouse}
                    open={Boolean(anchorElHouse)}
                    onClose={() => setAnchorElHouse(null)}
                  >
                    {selectedConsignment.houses.map((h, idx) => (
                      <MenuItem
                        key={idx}
                        selected={idx === selectedHouseIndex}
                        onClick={() => {
                          setSelectedHouseIndex(idx);
                          setAnchorElHouse(null);
                        }}
                      >
                        {`House ${idx + 1} - ${h.mrnReference || " "}`}
                      </MenuItem>
                    ))}
                    <Divider />
                    <MenuItem
                      onClick={() => {
                        setSelectedConsignment((prev) => {
                          const houses = [...(prev.houses ?? [])];
                          houses.push(defaultHouse());
                          return { ...prev, houses };
                        });
                        setSelectedHouseIndex(
                          selectedConsignment?.houses?.length ?? 0,
                        );
                        setAnchorElHouse(null);
                      }}
                      disabled={
                        loading ||
                        !selectedConsignment ||
                        selectedConsignment?.status === "SENT" ||
                        selectedConsignment?.status === "ACK" ||
                        selectedConsignment?.status === "APPROVED" ||
                        selectedConsignment?.status === "REJECTED" ||
                        selectedConsignment?.status === "DONE" ||
                        selectedConsignment?.customsOffice?.id == null
                      }
                    >
                      <Icon fontSize="small" style={{ marginRight: 8 }}>
                        add
                      </Icon>
                      Add house
                    </MenuItem>
                    <MenuItem
                      disabled={
                        (selectedConsignment?.houses?.length ?? 0) <= 1 ||
                        loading ||
                        !selectedConsignment ||
                        selectedConsignment?.status === "SENT" ||
                        selectedConsignment?.status === "ACK" ||
                        selectedConsignment?.status === "APPROVED" ||
                        selectedConsignment?.status === "REJECTED" ||
                        selectedConsignment?.status === "DONE"
                      }
                      onClick={() => {
                        setSelectedConsignment((prev) => {
                          const houses = [...(prev.houses ?? [])];
                          houses.splice(selectedHouseIndex, 1);
                          const newIndex = Math.max(0, selectedHouseIndex - 1);
                          setSelectedHouseIndex(newIndex);
                          return { ...prev, houses };
                        });
                        setAnchorElHouse(null);
                      }}
                    >
                      <Icon
                        fontSize="small"
                        style={{ marginRight: 8, color: "crimson" }}
                      >
                        delete
                      </Icon>
                      Remove current house
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setSelectedConsignment((prev) => {
                          const houses = [...(prev.houses ?? [])];
                          const clone = _.cloneDeep(
                            houses[selectedHouseIndex] ?? defaultHouse(),
                          );

                          // clear IDs so Hibernate/JPA will treat them as new
                          clone.id = null;
                          if (clone.goodsList) {
                            clone.goodsList.forEach((g) => (g.id = null));
                          }
                          if (clone.consignor) {
                            clone.consignor.id = null;
                          }
                          if (clone.consignee) {
                            clone.consignee.id = null;
                          }
                          if (clone.seller) {
                            clone.seller.id = null;
                          }
                          if (clone.buyer) {
                            clone.buyer.id = null;
                          }

                          houses.splice(selectedHouseIndex + 1, 0, clone);
                          return { ...prev, houses };
                        });
                        setSelectedHouseIndex((i) => i + 1);
                        setAnchorElHouse(null);
                      }}
                      disabled={
                        loading ||
                        !selectedConsignment ||
                        selectedConsignment?.status === "SENT" ||
                        selectedConsignment?.status === "ACK" ||
                        selectedConsignment?.status === "APPROVED" ||
                        selectedConsignment?.status === "REJECTED" ||
                        selectedConsignment?.status === "DONE" ||
                        selectedConsignment?.customsOffice?.id == null
                      }
                    >
                      <Icon fontSize="small" style={{ marginRight: 8 }}>
                        content_copy
                      </Icon>
                      Duplicate current house
                    </MenuItem>
                  </Menu>
                </div>
              </Tooltip>
            )}
          </Box>
        </Grid>

        {/* HOUSE PANELS */}
        <Grid item xs={12}>
          <Grid container spacing={4}>
            {/* Consignment Details (per-house MTD; consignment-level customs/MRN retained) */}
            <Grid item xs={12} md={4} lg={2.4}>
              <Paper elevation={3} style={{ padding: 10, minHeight: "38vh" }}>
                <Grid item xs={12}>
                  <Typography variant="h6">Document reference</Typography>
                </Grid>

                {/* Main transport document (always per-house) */}
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    size="small"
                    variant="outlined"
                    style={{ marginTop: "16px" }}
                  >
                    <InputLabel>{"Main transport document"}</InputLabel>
                    <Select
                      size="small"
                      value={selectedHouse?.mainTransportDocument || ""}
                      label={"Main transport document"}
                      onChange={(event) => {
                        handleHouseChange(
                          "mainTransportDocument",
                          event.target.value,
                        );
                        setIsDirty(true);
                      }}
                    >
                      {mainTransportDocuments?.map((code) => (
                        <MenuItem key={code} value={code}>
                          {mainTransportDocumentLabels[code] || code}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="House document reference"
                    variant="outlined"
                    fullWidth
                    size="small"
                    margin="normal"
                    value={selectedHouse?.mrnReference || ""}
                    onChange={(event) => {
                      handleHouseChange("mrnReference", event.target.value);
                      setIsDirty(true);
                    }}
                    error={err(`mrnReference`) !== undefined}
                    helperText={
                      err(`mrnReference`)
                        ? err(`mrnReference`)
                        : "Reference of the transport document(s) that covers the transport of goods into the customs territory of the Union. The unique number identifying your House shipment (e.g., CMR Invoice, ATA, TIR etc.). "
                    }
                  />
                </Grid>
              </Paper>
            </Grid>

            {selectedConsignment?.customsOffice?.direction === "GB" && (
              <>
                {/* Consignor panel (HOUSE) */}
                <Grid item xs={12} md={4} lg={2.4}>
                  <Paper
                    elevation={3}
                    style={{ padding: 10, minHeight: "38vh" }}
                  >
                    <Grid item xs={12}>
                      <Typography variant="h6">Consignor</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Name"
                        variant="outlined"
                        fullWidth
                        size="small"
                        margin="normal"
                        value={selectedHouse?.consignor?.name || ""}
                        onChange={(event) => {
                          handleHouseChange(
                            "consignor.name",
                            sanitize(event.target.value),
                          );
                          setIsDirty(true);
                        }}
                        error={err(`consignor.name`) !== undefined}
                        helperText={err(`consignor.name`)}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Autocomplete
                        noOptionsText={LOCALE.noOptionsText}
                        autoComplete={false}
                        size="small"
                        value={
                          selectedHouse?.consignor?.country?.name
                            ? selectedHouse?.consignor?.country?.name
                            : ""
                        }
                        onChange={(event, newValue) => {
                          handleHouseChange(
                            "consignor.country",
                            countries?.filter(
                              (country) => country.name === newValue,
                            )[0],
                          );
                          setIsDirty(true);
                        }}
                        inputValue={countriesSearchC1 ? countriesSearchC1 : ""}
                        onInputChange={(event, newInputValue) => {
                          setCountriesSearchC1(newInputValue);
                        }}
                        options={countries?.map((country) => country.name)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={LOCALE.country}
                            fullWidth
                            variant="outlined"
                            error={err(`consignor.country.name`) !== undefined}
                            helperText={err(`consignor.country.name`)}
                          />
                        )}
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      style={{
                        display: "inline-flex",
                      }}
                    >
                      <>
                        <Autocomplete
                          noOptionsText={LOCALE.noOptionsText}
                          autoComplete={false}
                          size="small"
                          value={
                            selectedHouse?.consignor?.city?.cityName
                              ? selectedHouse?.consignor?.city?.cityName
                              : ""
                          }
                          onChange={(event, newValue) => {
                            handleHouseChange(
                              "consignor.city",
                              citiesConsignor?.filter(
                                (item) => item.cityName === newValue,
                              )[0],
                            );
                            setIsDirty(true);
                          }}
                          inputValue={
                            citiesSearchConsignor ? citiesSearchConsignor : ""
                          }
                          onInputChange={(event, newInputValue) => {
                            setCitiesSearchConsignor(newInputValue);
                          }}
                          options={citiesConsignor?.map(
                            (item) => item.cityName,
                          )}
                          fullWidth
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={LOCALE.city}
                              style={{ width: "90%", marginRight: "5px" }}
                              variant="outlined"
                              size="small"
                              margin="normal"
                              error={
                                err(`consignor.city.cityName`) !== undefined
                              }
                              helperText={err(`consignor.city.cityName`)}
                            />
                          )}
                        />
                        <TextField
                          label="Post Code"
                          variant="outlined"
                          size="small"
                          margin="normal"
                          value={selectedHouse?.consignor?.postCode || ""}
                          onChange={(event) => {
                            handleHouseChange(
                              "consignor.postCode",
                              event.target.value,
                            );
                            setIsDirty(true);
                          }}
                          error={err(`consignor.postCode`) !== undefined}
                          helperText={err(`consignor.postCode`)}
                        />
                      </>
                    </Grid>

                    <Grid item md={12}>
                      <Button
                        variant="text"
                        color="info"
                        size="small"
                        onClick={() => {
                          setAddressFlagConsignor(!addressFlagConsignor);
                          setIsDirty(true);
                        }}
                        style={{ float: "right", marginBottom: "-20px" }}
                      >
                        {addressFlagConsignor ? "PO Box" : "Address"}
                      </Button>
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      style={{
                        display: addressFlagConsignor ? "inline-flex" : "",
                      }}
                    >
                      {addressFlagConsignor ? (
                        <>
                          <TextField
                            label="Address"
                            variant="outlined"
                            size="small"
                            style={{ width: "90%", marginRight: "5px" }}
                            margin="normal"
                            value={selectedHouse?.consignor?.address || ""}
                            onChange={(event) => {
                              handleHouseChange(
                                "consignor.address",
                                sanitize(event.target.value),
                              );
                              setIsDirty(true);
                            }}
                            error={err(`consignor.address`) !== undefined}
                            helperText={err(`consignor.address`)}
                          />

                          <TextField
                            label="Number"
                            variant="outlined"
                            size="small"
                            margin="normal"
                            value={
                              selectedHouse?.consignor?.addressNumber || ""
                            }
                            onChange={(event) => {
                              handleHouseChange(
                                "consignor.addressNumber",
                                sanitize(event.target.value),
                              );
                              setIsDirty(true);
                            }}
                            error={err(`consignor.addressNumber`) !== undefined}
                            helperText={err(`consignor.addressNumber`)}
                          />
                        </>
                      ) : (
                        <TextField
                          label="PO Box Number"
                          variant="outlined"
                          fullWidth
                          size="small"
                          margin="normal"
                          value={selectedHouse?.consignor?.poBoxNumber || ""}
                          onChange={(event) => {
                            handleHouseChange(
                              "consignor.poBoxNumber",
                              event.target.value,
                            );
                            setIsDirty(true);
                          }}
                          error={err(`consignor.poBoxNumber`) !== undefined}
                          helperText={err(`consignor.poBoxNumber`)}
                        />
                      )}
                    </Grid>
                  </Paper>
                </Grid>
              </>
            )}

            {/* Seller / Sender (HOUSE) */}
            <Grid item xs={12} md={4} lg={2.4}>
              <Paper elevation={3} style={{ padding: 10, minHeight: "38vh" }}>
                <Grid container>
                  <Grid item xs={6}>
                    <Typography variant="h6">
                      Seller / Sender
                      <Tooltip
                        arrow
                        placement="top"
                        title={
                          "The person or company that sells or dispatches the goods."
                        }
                      >
                        <Icon
                          fontSize="small"
                          color="action"
                          sx={{ ml: 0.5, verticalAlign: "middle" }}
                        >
                          help_outline
                        </Icon>
                      </Tooltip>
                    </Typography>
                  </Grid>
                  {selectedConsignment?.customsOffice?.direction === "GB" && (
                    <Grid item xs={6} style={{ textAlign: "right" }}>
                      <Button
                        variant="text"
                        color="info"
                        size="small"
                        onClick={() => {
                          copyConsignorToSeller();
                          setIsDirty(true);
                        }}
                      >
                        same as consignor
                      </Button>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <TextField
                      label="Name"
                      variant="outlined"
                      fullWidth
                      size="small"
                      margin="normal"
                      value={selectedHouse?.seller?.name || ""}
                      onChange={(event) => {
                        handleHouseChange(
                          "seller.name",
                          sanitize(event.target.value),
                        );
                        setIsDirty(true);
                      }}
                      error={err(`seller.name`) !== undefined}
                      helperText={err(`seller.name`)}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      noOptionsText={LOCALE.noOptionsText}
                      autoComplete={false}
                      size="small"
                      value={
                        selectedHouse?.seller?.country?.name
                          ? selectedHouse?.seller?.country?.name
                          : ""
                      }
                      onChange={(event, newValue) => {
                        handleHouseChange(
                          "seller.country",
                          countries?.filter(
                            (country) => country.name === newValue,
                          )[0],
                        );
                        setIsDirty(true);
                      }}
                      inputValue={countriesSearchS ? countriesSearchS : ""}
                      onInputChange={(event, newInputValue) => {
                        setCountriesSearchS(newInputValue);
                      }}
                      options={countries?.map((country) => country.name)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={LOCALE.country}
                          fullWidth
                          variant="outlined"
                          error={err(`seller.country.name`) !== undefined}
                          helperText={err(`seller.country.name`)}
                        />
                      )}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    style={{
                      display: "inline-flex",
                    }}
                  >
                    <Autocomplete
                      noOptionsText={LOCALE.noOptionsText}
                      autoComplete={false}
                      size="small"
                      value={
                        selectedHouse?.seller?.city?.cityName
                          ? selectedHouse?.seller?.city?.cityName
                          : ""
                      }
                      onChange={(event, newValue) => {
                        handleHouseChange(
                          "seller.city",
                          citiesSeller?.filter(
                            (item) => item.cityName === newValue,
                          )[0],
                        );
                        setIsDirty(true);
                      }}
                      inputValue={citiesSearchSeller ? citiesSearchSeller : ""}
                      onInputChange={(event, newInputValue) => {
                        setCitiesSearchSeller(newInputValue);
                      }}
                      options={citiesSeller?.map((item) => item.cityName)}
                      fullWidth
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={LOCALE.city}
                          style={{ width: "90%", marginRight: "5px" }}
                          variant="outlined"
                          size="small"
                          margin="normal"
                          error={err(`seller.city.cityName`) !== undefined}
                          helperText={err(`seller.city.cityName`)}
                        />
                      )}
                    />
                    <TextField
                      label="Post Code"
                      variant="outlined"
                      size="small"
                      margin="normal"
                      value={selectedHouse?.seller?.postCode || ""}
                      onChange={(event) => {
                        handleHouseChange(
                          "seller.postCode",
                          sanitize(event.target.value),
                        );
                        setIsDirty(true);
                      }}
                      error={err(`seller.postCode`) !== undefined}
                      helperText={err(`seller.postCode`)}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      variant="text"
                      color="info"
                      size="small"
                      onClick={() => {
                        setAddressFlagSeller(!addressFlagSeller);
                        setIsDirty(true);
                      }}
                      style={{ float: "right", marginBottom: "-20px" }}
                    >
                      {addressFlagSeller ? "PO Box" : "Address"}
                    </Button>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    style={{
                      display: addressFlagSeller ? "inline-flex" : "",
                    }}
                  >
                    {addressFlagSeller ? (
                      <>
                        <TextField
                          label="Address"
                          variant="outlined"
                          size="small"
                          style={{ width: "90%", marginRight: "5px" }}
                          margin="normal"
                          value={selectedHouse?.seller?.address || ""}
                          onChange={(event) => {
                            handleHouseChange(
                              "seller.address",
                              sanitize(event.target.value),
                            );
                            setIsDirty(true);
                          }}
                          error={err(`seller.address`) !== undefined}
                          helperText={err(`seller.address`)}
                        />

                        <TextField
                          label="Number"
                          variant="outlined"
                          size="small"
                          margin="normal"
                          value={selectedHouse?.seller?.addressNumber || ""}
                          onChange={(event) => {
                            handleHouseChange(
                              "seller.addressNumber",
                              sanitize(event.target.value),
                            );
                            setIsDirty(true);
                          }}
                          error={err(`seller.addressNumber`) !== undefined}
                          helperText={err(`seller.addressNumber`)}
                        />
                      </>
                    ) : (
                      <TextField
                        label="PO Box Number"
                        variant="outlined"
                        fullWidth
                        size="small"
                        margin="normal"
                        value={selectedHouse?.seller?.poBoxNumber || ""}
                        onChange={(event) => {
                          handleHouseChange(
                            "seller.poBoxNumber",
                            event.target.value,
                          );
                          setIsDirty(true);
                        }}
                        error={err(`seller.poBoxNumber`) !== undefined}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="EORI (optional)"
                      variant="outlined"
                      fullWidth
                      size="small"
                      margin="normal"
                      value={selectedHouse?.seller?.eori || ""}
                      onChange={(event) => {
                        handleHouseChange("seller.eori", event.target.value);
                        setIsDirty(true);
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Buyer / Receiver (HOUSE) */}
            <Grid item xs={12} md={4} lg={2.4}>
              <Paper elevation={3} style={{ padding: 10, minHeight: "38vh" }}>
                <Grid container>
                  <Grid item xs={6}>
                    <Typography variant="h6">
                      Buyer / Receiver{" "}
                      <Tooltip
                        arrow
                        placement="top"
                        title={
                          "The person or company that purchases or receives the goods."
                        }
                      >
                        <Icon
                          fontSize="small"
                          color="action"
                          sx={{ ml: 0.5, verticalAlign: "middle" }}
                        >
                          help_outline
                        </Icon>
                      </Tooltip>
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="Name"
                      variant="outlined"
                      fullWidth
                      size="small"
                      margin="normal"
                      value={selectedHouse?.buyer?.name || ""}
                      onChange={(event) => {
                        handleHouseChange(
                          "buyer.name",
                          sanitize(event.target.value),
                        );
                        setIsDirty(true);
                      }}
                      error={err(`buyer.name`) !== undefined}
                      helperText={err(`buyer.name`)}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      noOptionsText={LOCALE.noOptionsText}
                      autoComplete={false}
                      size="small"
                      value={
                        selectedHouse?.buyer?.country?.name
                          ? selectedHouse?.buyer?.country?.name
                          : ""
                      }
                      onChange={(event, newValue) => {
                        handleHouseChange(
                          "buyer.country",
                          countries?.filter(
                            (country) => country.name === newValue,
                          )[0],
                        );
                        setIsDirty(true);
                      }}
                      inputValue={countriesSearchB ? countriesSearchB : ""}
                      onInputChange={(event, newInputValue) => {
                        setCountriesSearchB(newInputValue);
                      }}
                      options={countries?.map((country) => country.name)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={LOCALE.country}
                          fullWidth
                          variant="outlined"
                          error={err(`buyer.country.name`) !== undefined}
                          helperText={err(`buyer.country.name`)}
                        />
                      )}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    style={{
                      display: "inline-flex",
                    }}
                  >
                    <Autocomplete
                      noOptionsText={LOCALE.noOptionsText}
                      autoComplete={false}
                      size="small"
                      value={
                        selectedHouse?.buyer?.city?.cityName
                          ? selectedHouse?.buyer?.city?.cityName
                          : ""
                      }
                      onChange={(event, newValue) => {
                        handleHouseChange(
                          "buyer.city",
                          citiesBuyer?.filter(
                            (item) => item.cityName === newValue,
                          )[0],
                        );
                        setIsDirty(true);
                      }}
                      inputValue={citiesSearchBuyer ? citiesSearchBuyer : ""}
                      onInputChange={(event, newInputValue) => {
                        setCitiesSearchBuyer(newInputValue);
                      }}
                      options={citiesBuyer?.map((item) => item.cityName)}
                      fullWidth
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={LOCALE.city}
                          style={{ width: "90%", marginRight: "5px" }}
                          variant="outlined"
                          size="small"
                          margin="normal"
                          error={err(`buyer.city.cityName`) !== undefined}
                          helperText={err(`buyer.city.cityName`)}
                        />
                      )}
                    />
                    <TextField
                      label="Post Code"
                      variant="outlined"
                      size="small"
                      margin="normal"
                      value={selectedHouse?.buyer?.postCode || ""}
                      onChange={(event) => {
                        handleHouseChange(
                          "buyer.postCode",
                          sanitize(event.target.value),
                        );
                        setIsDirty(true);
                      }}
                      error={err(`buyer.postCode`) !== undefined}
                      helperText={err(`buyer.postCode`)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="text"
                      color="info"
                      size="small"
                      onClick={() => {
                        setAddressFlagBuyer(!addressFlagBuyer);
                        setIsDirty(true);
                      }}
                      style={{ float: "right", marginBottom: "-20px" }}
                    >
                      {addressFlagBuyer ? "PO Box" : "Address"}
                    </Button>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    style={{
                      display: addressFlagBuyer ? "inline-flex" : "",
                    }}
                  >
                    {addressFlagBuyer ? (
                      <>
                        <TextField
                          label="Address"
                          variant="outlined"
                          size="small"
                          style={{ width: "90%", marginRight: "5px" }}
                          margin="normal"
                          value={selectedHouse?.buyer?.address || ""}
                          onChange={(event) => {
                            handleHouseChange(
                              "buyer.address",
                              sanitize(event.target.value),
                            );
                            setIsDirty(true);
                          }}
                          error={err(`buyer.address`) !== undefined}
                          helperText={err(`buyer.address`)}
                        />

                        <TextField
                          label="Number"
                          variant="outlined"
                          size="small"
                          margin="normal"
                          value={selectedHouse?.buyer?.addressNumber || ""}
                          onChange={(event) => {
                            handleHouseChange(
                              "buyer.addressNumber",
                              sanitize(event.target.value),
                            );
                            setIsDirty(true);
                          }}
                          error={err(`buyer.addressNumber`) !== undefined}
                          helperText={err(`buyer.addressNumber`)}
                        />
                      </>
                    ) : (
                      <TextField
                        label="PO Box Number"
                        variant="outlined"
                        fullWidth
                        size="small"
                        margin="normal"
                        value={selectedHouse?.buyer?.poBoxNumber || ""}
                        onChange={(event) => {
                          handleHouseChange(
                            "buyer.poBoxNumber",
                            event.target.value,
                          );
                          setIsDirty(true);
                        }}
                        error={err(`buyer.poBoxNumber`) !== undefined}
                        helperText={err(`buyer.poBoxNumber`)}
                      />
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="EORI (optional)"
                      variant="outlined"
                      fullWidth
                      size="small"
                      margin="normal"
                      value={selectedHouse?.buyer?.eori || ""}
                      onChange={(event) => {
                        handleHouseChange("buyer.eori", event.target.value);
                        setIsDirty(true);
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {selectedConsignment?.customsOffice?.direction === "GB" && (
              <>
                {/* Consignee (HOUSE) */}
                <Grid item xs={12} md={4} lg={2.4}>
                  <Paper
                    elevation={3}
                    style={{ padding: 10, minHeight: "38vh" }}
                  >
                    <Grid container>
                      <Grid item xs={6}>
                        <Typography variant="h6">Consignee</Typography>
                      </Grid>
                      <Grid item xs={6} style={{ textAlign: "right" }}>
                        <Button
                          variant="text"
                          color="info"
                          size="small"
                          onClick={() => {
                            copyBuyerToConsignee();
                            setIsDirty(true);
                          }}
                        >
                          same as buyer
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Name"
                          variant="outlined"
                          fullWidth
                          size="small"
                          margin="normal"
                          value={selectedHouse?.consignee?.name || ""}
                          onChange={(event) => {
                            handleHouseChange(
                              "consignee.name",
                              sanitize(event.target.value),
                            );
                            setIsDirty(true);
                          }}
                          error={err(`consignee.name`) !== undefined}
                          helperText={err(`consignee.name`)}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Autocomplete
                          noOptionsText={LOCALE.noOptionsText}
                          autoComplete={false}
                          size="small"
                          value={
                            selectedHouse?.consignee?.country?.name
                              ? selectedHouse?.consignee?.country?.name
                              : ""
                          }
                          onChange={(event, newValue) => {
                            handleHouseChange(
                              "consignee.country",
                              countries?.filter(
                                (country) => country.name === newValue,
                              )[0],
                            );
                            setIsDirty(true);
                          }}
                          inputValue={
                            countriesSearchC2 ? countriesSearchC2 : ""
                          }
                          onInputChange={(event, newInputValue) => {
                            setCountriesSearchC2(newInputValue);
                          }}
                          options={countries?.map((country) => country.name)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={LOCALE.country}
                              fullWidth
                              variant="outlined"
                              error={
                                err(`consignee.country.name`) !== undefined
                              }
                              helperText={err(`consignee.country.name`)}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} style={{ display: "inline-flex" }}>
                        <Autocomplete
                          noOptionsText={LOCALE.noOptionsText}
                          autoComplete={false}
                          size="small"
                          value={
                            selectedHouse?.consignee?.city?.cityName
                              ? selectedHouse?.consignee?.city?.cityName
                              : ""
                          }
                          onChange={(event, newValue) => {
                            handleHouseChange(
                              "consignee.city",
                              citiesConsignee?.filter(
                                (item) => item.cityName === newValue,
                              )[0],
                            );
                            setIsDirty(true);
                          }}
                          inputValue={
                            citiesSearchConsignee ? citiesSearchConsignee : ""
                          }
                          onInputChange={(event, newInputValue) => {
                            setCitiesSearchConsignee(newInputValue);
                          }}
                          options={citiesConsignee?.map(
                            (item) => item.cityName,
                          )}
                          fullWidth
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={LOCALE.city}
                              style={{ width: "90%", marginRight: "5px" }}
                              variant="outlined"
                              size="small"
                              margin="normal"
                              error={
                                err(`consignee.city.cityName`) !== undefined
                              }
                              helperText={err(`consignee.city.cityName`)}
                            />
                          )}
                        />
                        <TextField
                          label="Post Code"
                          variant="outlined"
                          size="small"
                          margin="normal"
                          value={selectedHouse?.consignee?.postCode || ""}
                          onChange={(event) => {
                            handleHouseChange(
                              "consignee.postCode",
                              sanitize(event.target.value),
                            );
                            setIsDirty(true);
                          }}
                          error={err(`consignee.postCode`) !== undefined}
                          helperText={err(`consignee.postCode`)}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Button
                          variant="text"
                          color="info"
                          size="small"
                          onClick={() => {
                            setAddressFlagConsignee(!addressFlagConsignee);
                            setIsDirty(true);
                          }}
                          style={{ float: "right", marginBottom: "-20px" }}
                        >
                          {addressFlagConsignee ? "PO Box" : "Address"}
                        </Button>
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        style={{
                          display: addressFlagConsignee ? "inline-flex" : "",
                        }}
                      >
                        {addressFlagConsignee ? (
                          <>
                            <TextField
                              label="Address"
                              variant="outlined"
                              size="small"
                              style={{ width: "90%", marginRight: "5px" }}
                              margin="normal"
                              value={selectedHouse?.consignee?.address || ""}
                              onChange={(event) => {
                                handleHouseChange(
                                  "consignee.address",
                                  sanitize(event.target.value),
                                );
                                setIsDirty(true);
                              }}
                              error={err(`consignee.address`) !== undefined}
                              helperText={err(`consignee.address`)}
                            />

                            <TextField
                              label="Number"
                              variant="outlined"
                              size="small"
                              margin="normal"
                              value={
                                selectedHouse?.consignee?.addressNumber || ""
                              }
                              onChange={(event) => {
                                handleHouseChange(
                                  "consignee.addressNumber",
                                  sanitize(event.target.value),
                                );
                                setIsDirty(true);
                              }}
                              error={
                                err(`consignee.addressNumber`) !== undefined
                              }
                              helperText={err(`consignee.addressNumber`)}
                            />
                          </>
                        ) : (
                          <TextField
                            label="PO Box Number"
                            variant="outlined"
                            fullWidth
                            size="small"
                            margin="normal"
                            value={selectedHouse?.consignee?.poBoxNumber || ""}
                            onChange={(event) => {
                              handleHouseChange(
                                "consignee.poBoxNumber",
                                event.target.value,
                              );
                              setIsDirty(true);
                            }}
                            error={err(`consignee.poBoxNumber`) !== undefined}
                            helperText={err(`consignee.poBoxNumber`)}
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </>
            )}

            {/* ITINERARY (HOUSE) && Place Acceptance/Delivery */}
            {selectedConsignment?.customsOffice?.direction === "EU" && (
              <>
                <Grid item xs={12} md={4} lg={2.4}>
                  <Paper
                    elevation={3}
                    style={{ padding: 10, minHeight: "38vh" }}
                  >
                    <Grid container>
                      <Grid item xs={6}>
                        <Typography variant="h6">
                          Itinerary
                          <Tooltip
                            arrow
                            placement="top"
                            title={
                              "Route or sequence of locations through which the goods will be transported from the place of loading to the place of delivery."
                            }
                          >
                            <Icon
                              fontSize="small"
                              color="action"
                              sx={{ ml: 0.5, verticalAlign: "middle" }}
                            >
                              help_outline
                            </Icon>
                          </Tooltip>
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Autocomplete
                          multiple
                          size="small"
                          options={countries}
                          getOptionLabel={(option) => option.name}
                          value={selectedHouse?.itineraryCountries ?? []}
                          onChange={(event, newValue) => {
                            handleHouseChange("itineraryCountries", newValue);
                            setIsDirty(true);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Itinerary countries"
                              variant="outlined"
                              margin="normal"
                              error={err("itineraryCountries") !== undefined}
                              helperText={err("itineraryCountries")}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4} lg={2.4}>
                  <Paper
                    elevation={3}
                    style={{ padding: 10, minHeight: "38vh" }}
                  >
                    <Grid container>
                      <Grid item xs={6}>
                        <Typography variant="h6">House Places</Typography>
                      </Grid>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={12}></Grid>
                        {/* Place of Acceptance */}
                        <Grid item xs={12} md={12}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              mb: 1,
                              display: "block",
                            }}
                          >
                            Place of Acceptance
                            <Tooltip
                              arrow
                              placement="top"
                              title={
                                "The location where the carrier takes over the goods from the shipper for transportation. "
                              }
                            >
                              <Icon
                                fontSize="small"
                                color="action"
                                sx={{ ml: 0.5, verticalAlign: "middle" }}
                              >
                                help_outline
                              </Icon>
                            </Tooltip>
                          </Typography>

                          <Grid container spacing={1}>
                            <Grid item xs={12}>
                              <Autocomplete
                                noOptionsText={LOCALE.noOptionsText}
                                autoComplete={false}
                                size="small"
                                value={
                                  selectedHouse?.placeOfAcceptance
                                    ?.countryName || ""
                                }
                                onChange={(event, newValue) => {
                                  handleHouseChange(
                                    "placeOfAcceptance.countryName",
                                    countries?.filter(
                                      (country) => country.name === newValue,
                                    )[0]?.name,
                                  );
                                  setIsDirty(true);
                                }}
                                inputValue={countriesSearchHPlA || ""}
                                onInputChange={(event, newInputValue) => {
                                  setCountriesSearchHPlA(newInputValue);
                                }}
                                options={countries?.map(
                                  (country) => country.name,
                                )}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label={LOCALE.country + " Acceptance"}
                                    fullWidth
                                    variant="outlined"
                                    error={
                                      err("placeOfAcceptance.countryName") !==
                                      undefined
                                    }
                                    helperText={err(
                                      "placeOfAcceptance.countryName",
                                    )}
                                  />
                                )}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <Autocomplete
                                noOptionsText={LOCALE.noOptionsText}
                                autoComplete={false}
                                size="small"
                                value={
                                  selectedHouse?.placeOfAcceptance?.cityName ||
                                  ""
                                }
                                onChange={(event, newValue) => {
                                  handleHouseChange(
                                    "placeOfAcceptance.cityName",
                                    citiesHPlOfAcceptance?.filter(
                                      (item) => item.cityName === newValue,
                                    )[0]?.cityName,
                                  );
                                  setIsDirty(true);
                                }}
                                inputValue={citiesSearchHPlOfAcceptance || ""}
                                onInputChange={(event, newInputValue) => {
                                  setCitiesSearchHPlOfAcceptance(newInputValue);
                                }}
                                options={citiesHPlOfAcceptance?.map(
                                  (item) => item.cityName,
                                )}
                                fullWidth
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label={LOCALE.city + " Acceptance"}
                                    variant="outlined"
                                    size="small"
                                    error={
                                      err("placeOfAcceptance.cityName") !==
                                      undefined
                                    }
                                    helperText={err(
                                      "placeOfAcceptance.cityName",
                                    )}
                                  />
                                )}
                              />
                            </Grid>
                          </Grid>
                        </Grid>

                        {/* Place of Delivery */}
                        <Grid item xs={12} md={12}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              mb: 1,
                              display: "block",
                            }}
                          >
                            Place of Delivery
                            <Tooltip
                              arrow
                              placement="top"
                              title={
                                "The location where the goods are handed over to the consignee at the end of the transport."
                              }
                            >
                              <Icon
                                fontSize="small"
                                color="action"
                                sx={{ ml: 0.5, verticalAlign: "middle" }}
                              >
                                help_outline
                              </Icon>
                            </Tooltip>
                          </Typography>

                          <Grid container spacing={1}>
                            <Grid item xs={12}>
                              <Autocomplete
                                noOptionsText={LOCALE.noOptionsText}
                                autoComplete={false}
                                size="small"
                                value={
                                  selectedHouse?.placeOfDelivery?.countryName ||
                                  ""
                                }
                                onChange={(event, newValue) => {
                                  handleHouseChange(
                                    "placeOfDelivery.countryName",
                                    countries?.filter(
                                      (country) => country.name === newValue,
                                    )[0]?.name,
                                  );
                                  setIsDirty(true);
                                }}
                                inputValue={countriesSearchHPlD || ""}
                                onInputChange={(event, newInputValue) => {
                                  setCountriesSearchHPlD(newInputValue);
                                }}
                                options={countries?.map(
                                  (country) => country.name,
                                )}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label={LOCALE.country + " Delivery"}
                                    fullWidth
                                    variant="outlined"
                                    error={
                                      err("placeOfDelivery.countryName") !==
                                      undefined
                                    }
                                    helperText={err(
                                      "placeOfDelivery.countryName",
                                    )}
                                  />
                                )}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <Autocomplete
                                noOptionsText={LOCALE.noOptionsText}
                                autoComplete={false}
                                size="small"
                                value={
                                  selectedHouse?.placeOfDelivery?.cityName || ""
                                }
                                onChange={(event, newValue) => {
                                  handleHouseChange(
                                    "placeOfDelivery.cityName",
                                    citiesHPlOfDelivery?.filter(
                                      (item) => item.cityName === newValue,
                                    )[0]?.cityName,
                                  );
                                  setIsDirty(true);
                                }}
                                inputValue={citiesSearchHPlOfDelivery || ""}
                                onInputChange={(event, newInputValue) => {
                                  setCitiesSearchHPlOfDelivery(newInputValue);
                                }}
                                options={citiesHPlOfDelivery?.map(
                                  (item) => item.cityName,
                                )}
                                fullWidth
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label={LOCALE.city + " Delivery"}
                                    variant="outlined"
                                    size="small"
                                    error={
                                      err("placeOfDelivery.cityName") !==
                                      undefined
                                    }
                                    helperText={err("placeOfDelivery.cityName")}
                                  />
                                )}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </>
            )}

            {/* Divider */}
            <Grid item xs={12}>
              <Divider style={{ width: "100%" }} />
            </Grid>

            {/* Goods Items (HOUSE) */}
            <GoodsItemsSection
              selectedHouse={selectedHouse}
              packageTypes={packageTypes}
              handleChangeRows={handleChangeRows}
              goodsErr={goodsErr}
              handleDeleteRow={handleDeleteRow}
              handleAddRow={handleAddRow}
              setIsDirty={setIsDirty}
            />
          </Grid>
        </Grid>
      </Grid>
      <DialogActions>
        {formDataErrors && Object.keys(formDataErrors).length > 0 && (
          // clear errors button
          <Button
            onClick={() => setFormDataErrors({})}
            variant="outlined"
            color="error"
            sx={{ marginRight: "20px" }}
          >
            Clear Errors
          </Button>
        )}
        <Button
          onClick={reject}
          color="error"
          startIcon={<Icon>save</Icon>}
          style={{ marginRight: "20px" }}
          disabled={
            loading ||
            !selectedConsignment ||
            selectedConsignment?.status !== "DRAFT"
          }
        >
          Reject
        </Button>
        <Button
          onClick={saveDraft}
          color="primary"
          startIcon={<Icon>save</Icon>}
          disabled={
            loading ||
            !selectedConsignment ||
            (selectedConsignment?.id && selectedConsignment.status != "DRAFT")
          }
        >
          Save as draft
        </Button>
        <Button
          onClick={save}
          color="success"
          startIcon={<Icon>save</Icon>}
          disabled={
            loading ||
            !selectedConsignment ||
            selectedConsignment?.status === "SENT" ||
            selectedConsignment?.status === "ACK" ||
            selectedConsignment?.status === "APPROVED" ||
            selectedConsignment?.status === "REJECTED" || 
            hasErrorAlert
          }
        >
          Save & Send
        </Button>
      </DialogActions>
      <CreateCarrierFromConsignmentDialog
        open={openAddNewCarrierDialog}
        onClose={() => setOpenAddNewCarrierDialog(false)}
        onSave={(carrier) => {
          // call API to create carrier, then close
          // CarrierRepository.create(carrier).then(()=> setOpenCreate(false));
          if (carrier.type === "CARRIER") {
            setCarriers((prev) => [...prev, carrier]);
            handleChange("carrier", carrier);
          } else if (carrier.type === "DECLARANT") {
            setDeclarants((prev) => [...prev, carrier]);
            handleChange("declarant", carrier);
          } else {
            setCarriers((prev) => [...prev, carrier]);
            setDeclarants((prev) => [...prev, carrier]);
            handleChange("carrier", carrier);
            // handleChange("declarant", carrier);
          }
        }}
        organization={selectedConsignment?.organization} // [{id, name}]
      />
    </>
  );
};
